// src/services/orchestrated-proposal-service.ts
// Orchestrated proposal generation service with gap analysis and progress tracking

import { supabase } from '../lib/supabase';
import type {
  SubmitabilityVerdict, GapReport, WinAssessment, ImprovementRoadmap, RequirementCoverage,
} from '../types/source-grounded';

// ============================================================================
// TYPES
// ============================================================================

export interface Gap {
  field: string;
  message: string;
  section: string;
  severity: 'critical' | 'recommended' | 'optional';
  // Enhanced gap context for semantic gaps
  requirementId?: string;           // Link to contract_requirements.id
  requirementText?: string;         // The actual requirement text
  requirementType?: string;         // technical, experience, certification, staffing, etc.
  suggestedAction?: string;         // What the user should do to address this gap
  relatedEvidence?: string[];       // What profile data was checked
  matchScore?: number;              // 0-100, how close the profile comes to meeting the requirement
}

export interface SemanticAnalysis {
  requirementsCoverage: number;           // 0-100, percentage of requirements matched
  strongMatches: number;                  // Requirements with >70% match
  partialMatches: number;                 // Requirements with 30-70% match
  unmetRequirements: number;              // Requirements with <30% match
  contractUpdatedSince?: string;          // ISO date if contract was updated since last analysis
  documentsNeedReextraction?: boolean;    // True if uploaded docs need processing
}

export interface ReadinessReport {
  ready: boolean;
  overallScore: number;
  collectedData: CollectedData;
  contract: ContractData;
  gaps: {
    critical: Gap[];
    recommended: Gap[];
    optional: Gap[];
  };
  matches: {
    naicsMatch: boolean;
    setAsideMatch: boolean;
    clearanceMatch: boolean;
    geographicMatch: boolean;
    experienceMatch: { matched: number; required: number };
  };
  recommendations: Array<{
    action: string;
    priority: 'critical' | 'high' | 'medium' | 'low';
    field?: string;
  }>;
  // Enhanced semantic analysis results
  semanticAnalysis?: SemanticAnalysis;
}

// Structured governing-rules profile extracted from the solicitation + attachments
// by the `extract-solicitation-profile` edge function. Every field is grounded in
// the documents; null / [] means "not stated" (never guessed).
export interface SolicitationProfile {
  vehicle?: {
    type?: string | null;
    isCommercialItem?: boolean | null;
    farPart?: string | null;
    guaranteedMinimum?: string | null;
    maximumValue?: string | null;
    sizeStandard?: string | null;
    orderingPeriod?: string | null;
    periods?: Array<{ label: string; months?: number | null }>;
  } | null;
  evaluation?: {
    method?: string | null;
    factorsInPriorityOrder?: Array<{ name: string; weight?: string | null; isGate?: boolean; notes?: string | null }>;
    priceEvaluation?: string | null;
    nonPriceVsPrice?: string | null;
  } | null;
  submission?: {
    volumes?: Array<{ name: string; factor?: string | null; pageLimit?: string | null; fileNaming?: string | null; notes?: string | null }>;
    formatRules?: string | null;
    pricingPlacementRule?: string | null;
    dueDateLocal?: string | null;
  } | null;
  pwsTasks?: Array<{ number?: string | null; title: string; summary?: string | null; deliverables?: string[] }>;
  technicalConstraints?: string[];
  security?: {
    tier?: string | null;
    phiAccess?: boolean | null;
    clearance?: string | null;
    trainings?: string[];
  } | null;
  keyPersonnel?: Array<{ role: string; required?: boolean; minQualifications?: string | null; certifications?: string[] }>;
  pastPerformance?: {
    maxReferences?: string | number | null;
    recencyYears?: string | number | null;
    relevanceCriteria?: string | null;
    ppqRequired?: boolean | null;
    submitTo?: string | null;
  } | null;
  pricing?: {
    instrument?: string | null;
    costBuildupProhibited?: boolean | null;
    clins?: Array<{
      clin?: string | null;
      code?: string | null;
      description?: string | null;
      unit?: string | null;
      quantity?: number | string | null;
      quantityNote?: string | null;
      periods?: string[];
      provenance?: string | null;
    }>;
    source?: string | null;
  } | null;
  repsCertsSignatures?: string[];
  integrityClauses?: string[];
  summary?: string | null;
}

export interface CollectedData {
  company: {
    name: string | null;
    address: {
      street: string | null;
      city: string | null;
      state: string | null;
      zip: string | null;
      full: string | null;
    };
    phone: string | null;
    email: string | null;
    website: string | null;
    duns: string | null;
    uei: string | null;
    cage: string | null;
    samStatus: string | null;
    legalStructure: string | null;
    yearEstablished: number | null;
    valueProposition: string | null;
    // Government contracting credentials
    naicsCodes: string[];
    primaryNaicsCode: string | null;
    // Additional company fields
    bio: string | null;
    fullName: string | null;
    title: string | null;
    securityClearanceLevel: string | null;
    facilityClearanceLevel: string | null;
    annualRevenue: number | null;
    totalEmployees: number | null;
    geographicServiceAreas: string[];
    certificationsHeld: string[];
    competitiveAdvantages: string[];
    differentiators: string[];
    coreCompetencies: string[];
    industriesServed: string[];
    keyTechnologies: string[];
  };
  capabilities: Array<{
    name: string;
    description: string;
    proficiencyLevel: string;
    yearsExperience: number;
    tools: string[];
  }>;
  pastPerformance: Array<{
    id: string;
    projectName: string;
    agency: string;
    contractValue: number;
    startDate: string;
    endDate: string;
    description: string;
    deliverables: string[];
    cparsRating: string | null;
    contractNumber: string | null;
    relevanceScore: number;
  }>;
  personnel: Array<{
    id: string;
    name: string;
    title: string;
    yearsExperience: number;
    certifications: string[];
    clearance: string | null;
    education: string[];
    relevantExperience: string;
    // Enhanced fields
    educationLevel?: string;
    skillsExpertise?: string[];
    keyAchievements?: string[];
    professionalSummary?: string;
    workHistory?: Array<{
      company: string;
      title: string;
      startDate: string;
      endDate: string;
      isCurrent?: boolean;
      description?: string;
      achievements?: string[];
      location?: string;
      employmentType?: string;
    }>;
    educationHistory?: Array<{
      institution: string;
      degree: string;
      field: string;
      graduationYear?: number;
      honors?: string;
      gpa?: string;
    }>;
    publications?: string[];
    languages?: string[];
    awards?: string[];
  }>;
  pricing: {
    labor: Array<{
      roleTitle: string;
      hourlyRate: number;
      loadedRate: number;
      laborCategory: string;
      // Grounding metadata from company_labor_categories (Profile v3) so rate
      // resolution can match by SCA occupation / SOC / functional family and
      // respect the company's key-personnel designation.
      scaTitle?: string | null;
      socCode?: string | null;
      functionalArea?: string | null;
      isKeyPersonnel?: boolean;
    }>;
    materials: Array<{
      description: string;
      unitCost: number;
      unit: string;
    }>;
    equipment: Array<{
      description: string;
      costType: string;
      costPerUnit: number;
    }>;
    fuel: Array<{
      fuelType: string;
      unitCost: number;
    }>;
    lease: Array<{
      description: string;
      periodicCost: number;
      leaseTerm: string;
    }>;
    overhead: Array<{
      description: string;
      rate: number;
      costType: string;
    }>;
  };
  certifications: string[];
  setAsides: string[];
  securityClearance: string | null;
  documents: Array<{
    id: string;
    name: string;
    type: string;
    extractedText: string;
  }>;
  // Contract-specific documents (solicitation docs uploaded by user)
  contractDocuments?: Array<{
    id: string;
    name: string;
    fileType: string;
    extractedText: string;
    status: string;
  }>;
  // Combined solicitation text for AI analysis
  solicitationText?: string;
  // Structured "governing rules" profile extracted from the solicitation + attachments.
  // This is the AUTHORITATIVE input that must control generation (vehicle, evaluation
  // order/gate, submission volumes/page limits, PWS task tree, technical constraints,
  // security tier, key personnel, past-performance mechanics, pricing/CLIN schedule,
  // reps/certs, integrity clauses). Produced by the extract-solicitation-profile fn.
  solicitationProfile?: SolicitationProfile | null;
  // Proposal plan generated from document analysis
  proposalPlan?: {
    solicitationRequirements: {
      compliance: Array<{ requirement: string; clause: string | null; mandatory: boolean }>;
      staffing: Array<{ role: string; quantity: number; qualifications: string[]; clearanceRequired: string | null }>;
      deliverables: Array<{ deliverable: string; dueDate: string | null; format: string | null }>;
      evaluationFactors: Array<{ factor: string; weight: number | null; description: string }>;
      technicalRequirements: string[];
      pricingStructure: { type: string | null; clin_items: string[]; estimatedValue: number | null };
    };
    strategy: {
      keyThemes: string[];
      winThemes: string[];
      discriminators: string[];
      risks: string[];
      mitigations: string[];
    };
    documentSources: Array<{ name: string; type: string; extractedTextLength: number }>;
  } | null;
  additionalContext: Record<string, string>;
}

export interface ContractData {
  id: string;
  title: string;
  agency: string;
  naicsCode: string | null;
  solicitationNumber: string | null;
  placeOfPerformance: {
    city: string | null;
    state: string | null;
    country: string | null;
  };
  requirements: Array<{
    id: string;
    text: string;
    category: string;
    priority: string;
  }>;
  evaluationCriteria: Array<{
    factor: string;
    weight: number;
    description: string;
  }>;
  sow: {
    background: string | null;
    scope: string | null;
    objectives: string[];
    tasks: string[];
    deliverables: string[];
  };
  setAsides: string[];
  dueDate: string | null;
  fundingAmount: number | null;
  periodOfPerformance: string | null;
  contractType: string | null;
}

export interface NewDataInput {
  documents?: Array<{
    filename: string;
    content: string; // base64
    mimeType: string;
  }>;
  textInputs?: Array<{
    text: string;
    associatedField: string;
  }>;
  structuredInputs?: Record<string, string>;
}

export interface GenerationProgress {
  phase: 'analyzing' | 'enriching' | 'generating' | 'complete' | 'error';
  currentSection?: string;
  completedSections: string[];
  totalSections: number;
  percentage: number;
  message: string;
}

export interface ProposalSection {
  sectionType: string;
  title: string;
  content: string;
  generatedAt: string;
}

// ============================================================================
// CHECKPOINT-GATED GENERATION (human-in-the-loop, resumable)
// ============================================================================

// Ordered generation gates. G0 (gap-resolution) is owned by GapResolutionPanel;
// the session is created already-approved at G0 the moment the user clicks Generate.
export type GenerationPhaseId =
  | 'gap-resolution'
  | 'backbone'
  | 'technical-management'
  | 'pp-personnel'
  | 'pricing-compliance'
  | 'summary-cover';

export const GENERATION_PHASE_ORDER: GenerationPhaseId[] = [
  'gap-resolution',
  'backbone',
  'technical-management',
  'pp-personnel',
  'pricing-compliance',
  'summary-cover',
];

// Human-readable labels + the sub-sections each gate produces, and the gates that
// depend on it (used by the review UI to explain "what this feeds").
export const GENERATION_PHASE_META: Record<GenerationPhaseId, {
  label: string;
  produces: string[];
  feeds: GenerationPhaseId[];
}> = {
  'gap-resolution': { label: 'Solicitation & Gaps', produces: ['Eligibility & gap resolution'], feeds: ['backbone', 'technical-management', 'pp-personnel', 'pricing-compliance', 'summary-cover'] },
  'backbone': { label: 'Deliverables, Labor & Cost', produces: ['Deliverables', 'Labor framework', 'Cost backbone'], feeds: ['technical-management', 'pricing-compliance', 'summary-cover'] },
  'technical-management': { label: 'Technical & Management', produces: ['Technical Approach', 'Management Approach'], feeds: ['summary-cover'] },
  'pp-personnel': { label: 'Past Performance & Personnel', produces: ['Past Performance', 'Key Personnel'], feeds: ['summary-cover'] },
  'pricing-compliance': { label: 'Pricing & Compliance', produces: ['Pricing', 'FAR/DFARS Compliance'], feeds: ['summary-cover'] },
  'summary-cover': { label: 'Executive Summary & Cover Letter', produces: ['Executive Summary', 'Cover Letter'], feeds: [] },
};

export type GenerationPhaseStatus =
  | 'not_started'
  | 'needs_input'   // generated but blocked on unanswered critical AI questions
  | 'generating'
  | 'awaiting_review'
  | 'approved'
  | 'error';

// Normalized AI clarifying question surfaced at a gate. A `critical` + unanswered
// question blocks approval of that phase. Today only the backbone (via
// derive-labor-rates) emits these; runPhase reads `clarifying_questions`
// defensively so other edge functions can emit them later.
export interface GenerationQuestion {
  id: string;
  phase: GenerationPhaseId;
  question: string;
  severity: 'critical' | 'optional';
  answer?: string | null;
  field?: string;
  whyItMatters?: string;
  currentAssumption?: string;
  inputType?: string;
  options?: string[] | null;
}

export interface GenerationPhaseState {
  status: GenerationPhaseStatus;
  payload?: any;                    // phase-specific generated output
  approved: boolean;
  approvedAt?: string | null;
  edits?: Record<string, string>;  // per-sub-section inline edits, applied at finalize
  error?: string | null;
  // Set when the user answers a question AFTER this phase was generated: the
  // answer only takes effect on the next runPhase, so approval is blocked until
  // the phase is regenerated.
  regenerateRequired?: boolean;
}

// In-memory shape of a generation session. Persisted to the
// `proposal_generation_sessions` table; `contract` lives inside the G0 phase
// payload on disk and is hydrated here for convenience.
export interface GenerationSession {
  id: string;
  profileId: string;
  noticeId: string;
  contractTitle?: string | null;
  status: 'active' | 'complete' | 'abandoned' | 'error';
  currentPhase: GenerationPhaseId | 'finalize';
  collectedData: CollectedData;
  solicitationProfile: SolicitationProfile | null;
  contract: ContractData;
  phases: Record<string, GenerationPhaseState>;
  questions: GenerationQuestion[];
  proposalTemplateId?: string | null;
}

export interface AIFeedback {
  overallAssessment: {
    summary: string;
    overallScore: number;
    winProbability: number;
    winProbabilityRationale: string;
  };
  sectionFeedback: {
    [key: string]: {
      score: number;
      strengths: string[];
      weaknesses: string[];
      recommendations: Array<{
        issue: string;
        recommendation: string;
        example: string;
        source: string;
      }>;
      complianceNotes: string;
    };
  };
  criticalIssues: Array<{
    issue: string;
    impact: string;
    recommendation: string;
    section: string;
  }>;
  prioritizedActions: Array<{
    priority: number;
    action: string;
    section: string;
    expectedImpact: string;
    timeEstimate: string;
  }>;
  evaluationCriteriaAlignment: Array<{
    criterion: string;
    weight: number;
    alignment: string;
    gaps: string[];
    recommendations: string[];
  }>;
  competitivePositioning: {
    strengths: string[];
    weaknesses: string[];
    differentiators: string[];
    risks: string[];
  };
  resourceSuggestions: Array<{
    need: string;
    source: string;
    section: string;
    priority: string;
  }>;
  generatedAt: string;
}

export interface GeneratedProposal {
  coverLetter: ProposalSection | null;
  executiveSummary: ProposalSection | null;
  technical: ProposalSection | null;
  management: ProposalSection | null;
  pastPerformance: ProposalSection | null;
  personnel: ProposalSection | null;
  pricing: ProposalSection | null;
  compliance: ProposalSection | null;
  aiFeedback: AIFeedback | null;
  deliverablesData: DeliverablesData | null;
}

// Deliverables data types
export interface DeliverablesData {
  deliverables_with_labor: DeliverableWithLabor[];
  labor_summary: LaborSummary;
  pricing_inputs: PricingInputs;
  // Non-labor / ODC roll-up (materials, hosting/storage, delivery, 508 compliance, etc.)
  non_labor_summary?: {
    by_category?: Array<{ category: string; estimated_cost?: number; items?: string[] }>;
    total_estimated_cost?: number;
    notes?: string;
  } | null;
  compliance_framework?: any;
  timeline_summary?: any;
  volume_summary?: any;
  product_definition?: any;
  // AI-derived pricing (rates, cost roll-up, and clarifying questions)
  ai_pricing?: AiPricing | null;
  metadata?: {
    generatedAt: string;
    source: string;
    deliverablesCount: number;
  };
}

// Output of the derive-labor-rates edge function
export interface AiPricing {
  rates: Array<{
    category: string;
    sca_title: string | null;
    assumed_seniority?: string;
    base_hourly: number;
    basis: string;
    confidence: string;
    rationale?: string;
  }>;
  indirect_assumptions: {
    fringe_pct: number;
    overhead_pct: number;
    ga_pct: number;
    fee_pct: number;
    basis?: string;
    user_confirmed?: boolean;
  };
  cost_rollup: {
    by_category: Array<{
      category: string;
      sca_title: string | null;
      hours: number;
      base_hourly: number;
      loaded_hourly: number;
      basis: string;
      confidence: string;
      direct_labor: number;
      rationale?: string | null;
    }>;
    direct_labor: number;
    fringe: number;
    overhead: number;
    ga: number;
    fee: number;
    total_price: number;
    total_hours: number;
    blended_loaded_rate: number;
  };
  clarifying_questions: Array<{
    id: string;
    field: string;
    question: string;
    why_it_matters: string;
    current_assumption: string;
    impact: 'high' | 'medium' | 'low';
    input_type: 'percent' | 'currency' | 'choice' | 'boolean' | 'text';
    options: string[] | null;
  }>;
  // Work locality that governed the rates + parsed SCA WD (see types/deliverables.ts).
  locality?: string | null;
  locality_source?: 'user-answer' | 'solicitation' | 'company-default' | null;
  sca_wage_determination?: {
    wd_number?: string | null;
    health_welfare_hourly?: number | null;
    categories_floored?: number;
  } | null;
  notes?: string | null;
}

export interface DeliverableWithLabor {
  deliverable_id: string;
  deliverable_name: string;
  description?: string;
  type?: string;
  labor_phases: Array<{
    phase_name: string;
    description: string;
    hours_estimate: { min: number; max: number; recommended: number };
    labor_categories: Array<{
      category: string;
      hours_percent: number;
      sca_title?: string;
    }>;
  }>;
  total_hours: { min: number; max: number; recommended: number };
  compliance_requirements?: any[];
  specifications?: any;
  timeline?: any;
}

export interface LaborSummary {
  by_category: Array<{
    category: string;
    total_hours: { min: number; max: number };
    sca_applicable: boolean;
    sca_title?: string;
  }>;
  total_hours: {
    min: number;
    max: number;
    recommended: number;
    ceiling?: number;
  };
}

export interface PricingInputs {
  labor_categories_required: string[];
  hours_matrix: Record<string, Record<string, number>>;
  suggested_rate_basis: 'SCA' | 'GSA' | 'market' | 'profile';
  sca_wage_determination_needed: boolean;
}

// ============================================================================
// FIELD ASSOCIATIONS FOR TEXT INPUT
// ============================================================================

export const FIELD_ASSOCIATIONS = [
  // Company Info
  { value: 'company.address', label: 'Company Address', category: 'Company' },
  { value: 'company.phone', label: 'Company Phone', category: 'Company' },
  { value: 'company.email', label: 'Company Email', category: 'Company' },
  { value: 'company.website', label: 'Company Website', category: 'Company' },
  { value: 'company.description', label: 'Company Description / Value Proposition', category: 'Company' },
  { value: 'company.name', label: 'Company Name', category: 'Company' },
  { value: 'company.bio', label: 'Company Bio', category: 'Company' },
  { value: 'company.duns', label: 'DUNS Number', category: 'Company' },
  { value: 'company.uei', label: 'UEI Number', category: 'Company' },
  { value: 'company.cage', label: 'CAGE Code', category: 'Company' },

  // Capabilities
  { value: 'capabilities.technical', label: 'Technical Capabilities', category: 'Capabilities' },
  { value: 'capabilities.management', label: 'Management Approach', category: 'Capabilities' },

  // Past Performance
  { value: 'past_performance.project', label: 'Past Performance Project', category: 'Past Performance' },
  { value: 'past_performance.reference', label: 'Reference Contact Info', category: 'Past Performance' },
  { value: 'past_performance.additional_context', label: 'Additional PP Context', category: 'Past Performance' },

  // Personnel
  { value: 'personnel.key_person', label: 'Key Personnel Bio', category: 'Personnel' },
  { value: 'personnel.org_chart', label: 'Organization Structure', category: 'Personnel' },

  // Pricing - CRITICAL: these are often needed to resolve gaps
  { value: 'pricing.labor_rates', label: 'Labor Rates (e.g., "Software Engineer: $95/hr")', category: 'Pricing' },
  { value: 'pricing.labor', label: 'Labor Categories & Rates', category: 'Pricing' },
  { value: 'pricing.materials', label: 'Materials/Supplies', category: 'Pricing' },
  { value: 'pricing.travel', label: 'Travel Costs', category: 'Pricing' },
  { value: 'pricing.overhead', label: 'Overhead/Indirect Rates (e.g., "25%")', category: 'Pricing' },

  // Compliance
  { value: 'compliance.certifications', label: 'Certifications (e.g., "ISO 9001, CMMI Level 3")', category: 'Compliance' },
  { value: 'compliance.clearances', label: 'Security Clearances', category: 'Compliance' },
  { value: 'security_clearance', label: 'Security Clearance Level', category: 'Compliance' },

  // NAICS and Industry
  { value: 'naics_code', label: 'NAICS Code(s)', category: 'Industry' },

  // Other
  { value: 'other.context', label: 'Additional Context', category: 'Other' }
];

// ============================================================================
// ORCHESTRATED PROPOSAL SERVICE
// ============================================================================

// A published-rate-card row, as consumed by derive-labor-rates for deterministic
// base-rate resolution (BLS OEWS-sourced; see migration 20260626_wage_reference_and_soc.sql).
export interface PublishedRateRow {
  category: string;
  aliases: string[];
  sca_title: string | null;
  level: 'junior' | 'mid' | 'senior';
  base_hourly: number;
}

// Process-lifetime cache of the rate card (small, ~42 rows; free authenticated read).
let _publishedRatesCache: PublishedRateRow[] | null = null;

export class OrchestratedProposalService {
  private progressCallback?: (progress: GenerationProgress) => void;

  constructor(onProgress?: (progress: GenerationProgress) => void) {
    this.progressCallback = onProgress;
  }

  /**
   * Load the canonical published labor-rate card (BLS OEWS-sourced) so pricing
   * resolves base rates deterministically from published wages instead of an LLM
   * guess. Cached for the session; failures degrade gracefully to [] (the edge
   * function then falls back to its LLM derivation).
   */
  private async loadPublishedRates(): Promise<PublishedRateRow[]> {
    if (_publishedRatesCache) return _publishedRatesCache;
    try {
      const { data, error } = await supabase
        .from('published_labor_rates')
        .select('category, aliases, sca_title, level, base_hourly');
      if (error) {
        console.warn('⚠️ Could not load published_labor_rates (continuing):', error.message);
        return [];
      }
      _publishedRatesCache = (data || []) as PublishedRateRow[];
      return _publishedRatesCache;
    } catch (e) {
      console.warn('⚠️ published_labor_rates load exception (continuing):', e);
      return [];
    }
  }

  /**
   * The company's default work locality (Costing Studio → Indirect Rates →
   * Work Locality). Drives locality labor rates (SCA WDs are county-scoped)
   * when the solicitation doesn't dictate an on-site place of performance.
   * Fetched live (not from the session's collected_data snapshot) so sessions
   * created before the field existed — and locality changes mid-proposal —
   * are picked up by regenerate/reprice. RLS scopes the query to the user's row.
   */
  private async loadWorkLocality(): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('company_indirect_rates')
        .select('work_city, work_state, work_county')
        .limit(1)
        .maybeSingle();
      if (error || !data) return null;
      const cityState = [data.work_city, data.work_state].filter(Boolean).join(', ');
      if (!cityState) return null;
      return data.work_county ? `${cityState} (${data.work_county} County)` : cityState;
    } catch {
      return null;
    }
  }

  private updateProgress(progress: GenerationProgress) {
    if (this.progressCallback) {
      this.progressCallback(progress);
    }
  }

  /**
   * Step 1: Analyze readiness and get gap report
   */
  async analyzeReadiness(contractId: string, profileId: string): Promise<ReadinessReport> {
    this.updateProgress({
      phase: 'analyzing',
      completedSections: [],
      totalSections: 8,
      percentage: 5,
      message: 'Analyzing contract requirements and profile data...'
    });

    const { data, error } = await supabase.functions.invoke('proposal-orchestrator', {
      body: {
        mode: 'analyze',
        contractId,
        profileId
      }
    });

    if (error) {
      throw new Error(`Analysis failed: ${error.message}`);
    }

    this.updateProgress({
      phase: 'analyzing',
      completedSections: [],
      totalSections: 8,
      percentage: 10,
      message: 'Analysis complete. Review gaps before proceeding.'
    });

    return data as ReadinessReport;
  }

  /**
   * Step 2: Enrich with new data and re-analyze
   */
  async enrichAndReanalyze(
    contractId: string,
    profileId: string,
    newData: NewDataInput
  ): Promise<ReadinessReport> {
    this.updateProgress({
      phase: 'enriching',
      completedSections: [],
      totalSections: 8,
      percentage: 5,
      message: 'Processing new data and re-analyzing...'
    });

    const { data, error } = await supabase.functions.invoke('proposal-orchestrator', {
      body: {
        mode: 'enrich',
        contractId,
        profileId,
        newData
      }
    });

    if (error) {
      throw new Error(`Enrichment failed: ${error.message}`);
    }

    this.updateProgress({
      phase: 'enriching',
      completedSections: [],
      totalSections: 8,
      percentage: 10,
      message: 'Data processed. Review updated gaps.'
    });

    return data as ReadinessReport;
  }

  // Helper to add timeout to promises
  private withTimeout<T>(promise: Promise<T>, timeoutMs: number, operation: string): Promise<T> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error(`${operation} timed out after ${timeoutMs / 1000} seconds`));
      }, timeoutMs);

      promise
        .then(result => {
          clearTimeout(timer);
          resolve(result);
        })
        .catch(err => {
          clearTimeout(timer);
          reject(err);
        });
    });
  }

  /**
   * Step 3: Generate all proposal sections
   */
  async generateProposal(
    collectedData: CollectedData,
    contract: ContractData,
    acknowledgedGaps: string[] = []
  ): Promise<GeneratedProposal> {
    console.log('🚀 Starting proposal generation...');
    console.log('📊 CollectedData keys:', Object.keys(collectedData));
    console.log('📊 Contract:', contract.title);
    console.log('📄 Solicitation text length:', collectedData.solicitationText?.length || 0);
    console.log('📋 Proposal plan available:', !!collectedData.proposalPlan);

    // ── ROOT-CAUSE FIX ────────────────────────────────────────────────────────
    // Extract the solicitation's GOVERNING RULES (vehicle, evaluation order/gate,
    // submission volumes/page limits, PWS task tree, technical constraints,
    // security tier, key personnel, past-performance mechanics, CLIN schedule,
    // reps/certs, integrity clauses) and make them the controlling input for every
    // section. Previously the generator only saw truncated raw text and filled gaps
    // with a fixed house template — that is what produced an off-base proposal.
    // Best-effort: if extraction fails we fall back to the prior behavior.
    if (!collectedData.solicitationProfile) {
      this.updateProgress({
        phase: 'analyzing',
        completedSections: [],
        totalSections: 9,
        percentage: 8,
        message: 'Reading the solicitation’s governing rules...'
      });
      try {
        collectedData.solicitationProfile = await this.withTimeout(
          this.extractSolicitationProfile(collectedData, contract),
          120000,
          'Solicitation profile extraction'
        );
        const p = collectedData.solicitationProfile;
        console.log('📐 Solicitation profile extracted:', p ? {
          vehicle: p.vehicle?.type,
          farPart: p.vehicle?.farPart,
          volumes: p.submission?.volumes?.length || 0,
          pwsTasks: p.pwsTasks?.length || 0,
          evalFactors: p.evaluation?.factorsInPriorityOrder?.length || 0
        } : 'none');
      } catch (profileError) {
        console.warn('⚠️ Solicitation profile extraction failed (continuing with raw text):', profileError);
        collectedData.solicitationProfile = null;
      }
    }

    const sections = [
      'cover_letter',
      'executive_summary',
      'technical',
      'management',
      'past_performance',
      'personnel',
      'deliverables',
      'pricing',
      'compliance'
    ];

    const completedSections: string[] = [];
    const proposal: GeneratedProposal = {
      coverLetter: null,
      executiveSummary: null,
      technical: null,
      management: null,
      pastPerformance: null,
      personnel: null,
      pricing: null,
      compliance: null,
      aiFeedback: null,
      deliverablesData: null
    };

    this.updateProgress({
      phase: 'generating',
      completedSections: [],
      totalSections: sections.length,
      percentage: 15,
      message: 'Starting proposal generation...'
    });

    // Per-section timeout. Federal sections on Opus with adaptive thinking + a large
    // solicitation can take well over 2 minutes; the previous 120s cap was DISCARDING
    // sections that had actually completed, which forced a full (token-burning) retry.
    const GENERATION_TIMEOUT = 300000; // 5 minutes

    // Generate sections in DEPENDENCY ORDER. The substantive sections are produced
    // first; the Cover Letter and Executive Summary are generated LAST so they can
    // summarize the ACTUAL drafted proposal (technical/management/past-performance/
    // pricing) instead of guessing from the solicitation alone.
    try {
      // Step 1: Deliverables backbone FIRST — its requirements → deliverables → labor +
      // ODC backbone grounds Technical/Management AND informs Pricing.
      this.updateProgress({
        phase: 'generating',
        currentSection: 'deliverables',
        completedSections,
        totalSections: sections.length,
        percentage: 20,
        message: 'Analyzing deliverables, labor, and costs...'
      });
      console.log('🚨🚨🚨 ORCHESTRATED DELIVERABLES BACKBONE STARTING 🚨🚨🚨');
      const deliverablesResult = await this.generateDeliverablesAnalysis(collectedData, contract).catch((deliverableError) => {
        // Deliverables analysis is optional - don't fail the whole proposal
        console.warn('⚠️ Deliverables generation warning (continuing without):', deliverableError);
        return null;
      });
      proposal.deliverablesData = deliverablesResult;
      completedSections.push('deliverables');
      console.log(`✅ Deliverables backbone: ${deliverablesResult?.deliverables_with_labor?.length || 0} deliverables, $${(deliverablesResult as any)?.non_labor_summary?.total_estimated_cost?.toLocaleString?.() || 0} ODCs`);

      this.updateProgress({
        phase: 'generating',
        currentSection: 'technical',
        completedSections,
        totalSections: sections.length,
        percentage: 40,
        message: 'Generating Technical & Management Approach...'
      });

      // Step 2: Technical + Management (single call returns both) — grounded in the backbone
      const techMgmtResult = await this.generateTechnicalManagement(collectedData, contract, proposal.deliverablesData);
      proposal.technical = techMgmtResult.technical;
      completedSections.push('technical');
      proposal.management = techMgmtResult.management;
      completedSections.push('management');

      this.updateProgress({
        phase: 'generating',
        currentSection: 'past_performance',
        completedSections,
        totalSections: sections.length,
        percentage: 58,
        message: 'Generating Past Performance & Personnel...'
      });

      // Step 3: Past Performance + Personnel (single call returns both)
      const ppPersonnelResult = await this.generatePastPerformancePersonnel(collectedData, contract);
      proposal.pastPerformance = ppPersonnelResult.pastPerformance;
      completedSections.push('past_performance');
      proposal.personnel = ppPersonnelResult.personnel;
      completedSections.push('personnel');

      this.updateProgress({
        phase: 'generating',
        currentSection: 'pricing',
        completedSections,
        totalSections: sections.length,
        percentage: 72,
        message: 'Generating Pricing & Compliance...'
      });

      // Step 4: Pricing + Compliance (single call returns both) - driven by deliverables data
      const pricingComplianceResult = await this.generatePricingCompliance(collectedData, contract, proposal.deliverablesData);
      proposal.pricing = pricingComplianceResult.pricing;
      completedSections.push('pricing');
      proposal.compliance = pricingComplianceResult.compliance;
      completedSections.push('compliance');

      // Step 5: Cover Letter + Executive Summary LAST — they SUMMARIZE the proposal, so
      // we feed them a digest of everything drafted above for an accurate, specific result.
      this.updateProgress({
        phase: 'generating',
        currentSection: 'executive_summary',
        completedSections,
        totalSections: sections.length,
        percentage: 85,
        message: 'Writing Executive Summary & Cover Letter from the drafted proposal...'
      });
      const proposalDigest = this.buildGeneratedSectionsDigest(proposal);
      // Re-synthesize the Product from the full proposal now that all sections exist
      // (the backbone product was synthesized from deliverables alone).
      try {
        const richProduct = await this.synthesizeFullProduct(
          collectedData, contract, proposal.deliverablesData, proposalDigest
        );
        if (richProduct?.product_name && proposal.deliverablesData) {
          proposal.deliverablesData.product_definition = richProduct;
        }
      } catch (e) {
        console.warn('⚠️ Full product re-synthesis skipped (legacy path):', e);
      }
      const [execSummaryResult, coverLetterResult] = await Promise.all([
        this.withTimeout(
          this.generateExecutiveSummary(collectedData, contract, proposalDigest),
          GENERATION_TIMEOUT,
          'Executive Summary generation'
        ),
        this.withTimeout(
          this.generateCoverLetter(collectedData, contract, proposalDigest),
          GENERATION_TIMEOUT,
          'Cover Letter generation'
        )
      ]);
      proposal.executiveSummary = execSummaryResult;
      completedSections.push('executive_summary');
      proposal.coverLetter = coverLetterResult;
      completedSections.push('cover_letter');

      // Group 5: AI Feedback - analyze all generated sections
      this.updateProgress({
        phase: 'generating',
        currentSection: 'ai_feedback',
        completedSections,
        totalSections: sections.length + 1, // +1 for AI feedback
        percentage: 90,
        message: 'Generating AI feedback and recommendations...'
      });

      try {
        const aiFeedbackResult = await this.generateAIFeedback(collectedData, contract, proposal, acknowledgedGaps);
        proposal.aiFeedback = aiFeedbackResult;
      } catch (feedbackError) {
        // AI feedback is optional - don't fail the whole proposal if it fails
        console.error('AI feedback generation failed (continuing without):', feedbackError);
        proposal.aiFeedback = null;
      }

      this.updateProgress({
        phase: 'complete',
        completedSections: [...completedSections, 'ai_feedback'],
        totalSections: sections.length + 1,
        percentage: 100,
        message: 'Proposal generation complete!'
      });

      return proposal;

    } catch (error: any) {
      this.updateProgress({
        phase: 'error',
        completedSections,
        totalSections: sections.length,
        percentage: (completedSections.length / sections.length) * 100,
        message: `Error: ${error.message}`
      });
      throw error;
    }
  }

  // ============================================================================
  // SECTION GENERATORS
  // ============================================================================

  // Call the extract-solicitation-profile edge function to distill the governing
  // rules from the solicitation + its uploaded attachments.
  private async extractSolicitationProfile(
    collectedData: CollectedData,
    contract: ContractData
  ): Promise<SolicitationProfile | null> {
    const attachments = (collectedData.contractDocuments || [])
      .filter(d => (d.extractedText || '').trim().length > 0)
      .map(d => ({ name: d.name, text: d.extractedText }));

    // If we have neither combined text nor attachments, there is nothing to extract.
    if (!(collectedData.solicitationText || '').trim() && attachments.length === 0) {
      console.log('ℹ️ No solicitation text/attachments available; skipping profile extraction');
      return null;
    }

    const { data, error } = await supabase.functions.invoke('extract-solicitation-profile', {
      body: {
        contract: {
          title: contract.title,
          agency: contract.agency,
          naics: contract.naicsCode,
          noticeId: contract.solicitationNumber
        },
        solicitationText: collectedData.solicitationText || '',
        attachments
      }
    });

    if (error) {
      throw new Error(`Profile extraction failed: ${error.message}`);
    }
    if (!data?.success || !data?.profile) {
      throw new Error(`Profile extraction returned no profile: ${data?.error || 'unknown'}`);
    }
    return data.profile as SolicitationProfile;
  }

  // Render the structured profile into a compact, AUTHORITATIVE rules block that is
  // prepended to the solicitation context for every section. This is what forces the
  // solicitation (not a house template) to control the output, and it carries the
  // grounding directive that forbids uncited hard claims.
  private renderGoverningRules(profile: SolicitationProfile | null | undefined): string {
    if (!profile) return '';
    const lines: string[] = [];
    const L = (s: string) => lines.push(s);
    const has = (v: any) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);

    L('===== GOVERNING SOLICITATION RULES (AUTHORITATIVE) =====');
    L('These rules are extracted directly from THIS solicitation and its attachments.');
    L('They OVERRIDE any default template, house format, or generic assumption. Where a');
    L('rule below conflicts with a generic best practice, follow the rule. Do NOT invent');
    L('facts to fill gaps — if something is not stated, write "[DATA NEEDED]" rather than');
    L('guessing.');
    L('');

    const v = profile.vehicle;
    if (v && (has(v.type) || has(v.farPart) || has(v.guaranteedMinimum) || has(v.maximumValue) || has(v.sizeStandard) || has(v.periods))) {
      L('CONTRACT VEHICLE & SCALE:');
      if (has(v.type)) L(`- Vehicle type: ${v.type}`);
      if (v.isCommercialItem === true) L('- Commercial item acquisition (FAR Part 12/13): NO certified cost-or-pricing data, NO FAR 15 cost buildup.');
      if (has(v.farPart)) L(`- FAR Part: ${v.farPart}`);
      if (has(v.guaranteedMinimum)) L(`- Guaranteed minimum: ${v.guaranteedMinimum} (the order floor — total price must not fall below this).`);
      if (has(v.maximumValue)) L(`- Maximum value / ceiling: ${v.maximumValue}`);
      if (has(v.sizeStandard)) L(`- SBA size standard (a revenue threshold for eligibility — NOT the contract value): ${v.sizeStandard}`);
      if (has(v.orderingPeriod)) L(`- Ordering period: ${v.orderingPeriod}`);
      if (has(v.periods)) L(`- Pricing periods (use EXACTLY these, not "Base + 2 options"): ${(v.periods || []).map(p => p.label).join(', ')}`);
      L('');
    }

    const ev = profile.evaluation;
    if (ev && (has(ev.method) || has(ev.factorsInPriorityOrder) || has(ev.priceEvaluation))) {
      L('EVALUATION (allocate effort by these weights, in this order):');
      if (has(ev.method)) L(`- Method: ${ev.method}`);
      if (has(ev.factorsInPriorityOrder)) {
        (ev.factorsInPriorityOrder || []).forEach((f, i) => {
          const gate = f.isGate ? ' [GATE — if not acceptable here, nothing else is evaluated]' : '';
          const w = has(f.weight) ? ` (${f.weight})` : '';
          L(`  ${i + 1}. ${f.name}${w}${gate}${has(f.notes) ? ` — ${f.notes}` : ''}`);
        });
      }
      if (has(ev.nonPriceVsPrice)) L(`- Non-price vs price: ${ev.nonPriceVsPrice}`);
      if (has(ev.priceEvaluation)) L(`- Price evaluation: ${ev.priceEvaluation}`);
      L('');
    }

    const sub = profile.submission;
    if (sub && (has(sub.volumes) || has(sub.formatRules) || has(sub.pricingPlacementRule) || has(sub.dueDateLocal))) {
      L('SUBMISSION STRUCTURE (the proposal must be organized as these SEPARATE volumes):');
      (sub.volumes || []).forEach(vol => {
        const parts = [vol.name];
        if (has(vol.factor)) parts.push(`factor: ${vol.factor}`);
        if (has(vol.pageLimit)) parts.push(`page limit: ${vol.pageLimit}`);
        if (has(vol.fileNaming)) parts.push(`file naming: ${vol.fileNaming}`);
        if (has(vol.notes)) parts.push(vol.notes as string);
        L(`- ${parts.join(' | ')}`);
      });
      if (has(sub.pricingPlacementRule)) L(`- PRICING PLACEMENT: ${sub.pricingPlacementRule}`);
      if (has(sub.formatRules)) L(`- Format rules: ${sub.formatRules}`);
      if (has(sub.dueDateLocal)) L(`- Due: ${sub.dueDateLocal}`);
      L('');
    }

    if (has(profile.pwsTasks)) {
      L('PWS TASK TREE (the Technical volume must address EACH of these tasks, by number — do NOT invent tasks):');
      (profile.pwsTasks || []).forEach(t => {
        L(`- ${has(t.number) ? t.number + ' ' : ''}${t.title}${has(t.summary) ? `: ${t.summary}` : ''}`);
        (t.deliverables || []).forEach(d => L(`    • deliverable: ${d}`));
      });
      L('');
    }

    if (has(profile.technicalConstraints)) {
      L('TECHNICAL CONSTRAINTS (mandatory — do not contradict these):');
      (profile.technicalConstraints || []).forEach(c => L(`- ${c}`));
      L('');
    }

    const sec = profile.security;
    if (sec && (has(sec.tier) || has(sec.clearance) || sec.phiAccess !== null || has(sec.trainings))) {
      L('SECURITY:');
      if (has(sec.tier)) L(`- Tier: ${sec.tier}`);
      if (has(sec.clearance)) L(`- Clearance: ${sec.clearance}`);
      if (sec.phiAccess === false) L('- NO PHI access (do not assert HIPAA/PHI handling, PIA, or higher clearances than stated).');
      if (sec.phiAccess === true) L('- PHI access applies.');
      if (has(sec.trainings)) L(`- Required trainings: ${(sec.trainings || []).join(', ')}`);
      L('');
    }

    if (has(profile.keyPersonnel)) {
      L('REQUIRED KEY PERSONNEL (address these exact roles + minimums; do not substitute unrelated roles):');
      (profile.keyPersonnel || []).forEach(kp => {
        const reqd = kp.required === false ? ' (optional)' : ' (required)';
        const certs = has(kp.certifications) ? ` — certs: ${(kp.certifications || []).join(', ')}` : '';
        L(`- ${kp.role}${reqd}: ${has(kp.minQualifications) ? kp.minQualifications : ''}${certs}`);
      });
      L('');
    }

    const pp = profile.pastPerformance;
    if (pp && (has(pp.maxReferences) || has(pp.recencyYears) || has(pp.relevanceCriteria) || pp.ppqRequired !== null)) {
      L('PAST PERFORMANCE MECHANICS:');
      if (has(pp.maxReferences)) L(`- Max references: ${pp.maxReferences}`);
      if (has(pp.recencyYears)) L(`- Recency: within ${pp.recencyYears} years`);
      if (has(pp.relevanceCriteria)) L(`- Relevance: ${pp.relevanceCriteria}`);
      if (pp.ppqRequired === true) L(`- PPQ required${has(pp.submitTo) ? ` — submitted by references to: ${pp.submitTo}` : ''}.`);
      L('');
    }

    const pr = profile.pricing;
    if (pr && (has(pr.instrument) || has(pr.clins) || has(pr.source) || pr.costBuildupProhibited !== null)) {
      L('PRICING ARTIFACT:');
      if (has(pr.instrument)) L(`- Instrument: ${pr.instrument}`);
      if (has(pr.source)) L(`- Source: ${pr.source}`);
      if (pr.costBuildupProhibited === true) L('- Cost buildup PROHIBITED: do NOT produce a FAR Table 15-2 cost narrative / indirect-rate BOE. Provide unit + total prices per CLIN for each period.');
      if (has(pr.clins)) {
        L('- CLIN schedule:');
        (pr.clins || []).forEach(c => L(`    • ${has(c.clin) ? c.clin + ' ' : ''}${c.description || ''}${has(c.unit) ? ` [${c.unit}]` : ''}${has(c.quantityNote) ? ` (${c.quantityNote})` : ''}${has(c.periods) ? ` periods: ${(c.periods || []).join(', ')}` : ''}`));
      }
      L('');
    }

    if (has(profile.repsCertsSignatures)) {
      L('REQUIRED REPS / CERTS / SIGNATURES:');
      (profile.repsCertsSignatures || []).forEach(r => L(`- ${r}`));
      L('');
    }

    if (has(profile.integrityClauses)) {
      L('INTEGRITY CLAUSES (misrepresentation carries legal penalties — every factual claim must be true and sourced):');
      (profile.integrityClauses || []).forEach(c => L(`- ${c}`));
      L('');
    }

    L('GROUNDING DIRECTIVE: Do NOT assert credentials, certifications, clearances, CPARS');
    L('ratings, named tools/platforms, or quantified metrics ("documented 38%") unless they');
    L('are present in the company evidence provided to you. If evidence is missing, write');
    L('"[DATA NEEDED]" instead of inventing a fact. Use ONLY the contract vehicle, periods,');
    L('evaluation order, PWS tasks, constraints, and pricing instrument stated above.');
    L('===== END GOVERNING SOLICITATION RULES =====');
    return lines.join('\n');
  }

  // Build a compact digest of the already-drafted sections so the Cover Letter and
  // Executive Summary (generated LAST) can summarize the ACTUAL proposal instead of
  // guessing from the solicitation alone.
  private buildGeneratedSectionsDigest(proposal: GeneratedProposal): string {
    const toText = (content: any): string => {
      if (!content) return '';
      if (typeof content !== 'string') {
        try { return JSON.stringify(content); } catch { return String(content); }
      }
      // content may itself be a JSON string of structured data; flatten if so.
      try {
        const o = JSON.parse(content);
        return typeof o === 'string' ? o : JSON.stringify(o);
      } catch { return content; }
    };
    const parts: string[] = [];
    const add = (title: string, content: any, limit: number) => {
      const t = toText(content).replace(/\s+/g, ' ').trim();
      if (t) parts.push(`### ${title}\n${t.slice(0, limit)}`);
    };
    add('Technical Approach', proposal.technical?.content, 3000);
    add('Management Approach', proposal.management?.content, 2500);
    add('Past Performance', proposal.pastPerformance?.content, 2000);
    add('Key Personnel', proposal.personnel?.content, 2000);
    // Pricing: headline figures only (avoid dumping the full structured table).
    const pricingText = toText(proposal.pricing?.content);
    if (pricingText) {
      const figures = pricingText.match(/\$[\d,]+(?:\.\d+)?/g);
      const fig = figures ? ` Key figures: ${[...new Set(figures)].slice(0, 5).join(', ')}.` : '';
      parts.push(`### Pricing & Cost\nFirm pricing prepared per the solicitation's instrument.${fig}`);
    }
    const delivCount = proposal.deliverablesData?.deliverables_with_labor?.length || 0;
    if (delivCount) parts.push(`### Deliverables\n${delivCount} priced deliverables with labor mapping.`);
    return parts.join('\n\n');
  }

  // Helper to build solicitation context for AI
  private buildSolicitationContext(collectedData: CollectedData, maxLength: number = 10000): {
    solicitationText: string;
    proposalPlan: CollectedData['proposalPlan'];
    documentSummary: string;
    governingRules: string;
  } {
    // Render the authoritative governing-rules block (never truncated). This is the
    // controlling input. It is returned SEPARATELY and the edge functions inject it
    // explicitly into their prompts — so we do NOT also embed it in solicitationText
    // (that duplicated a large block into an already-huge payload and slowed/timed out
    // generation while burning tokens).
    const governingRules = this.renderGoverningRules(collectedData.solicitationProfile);

    // Fold in any user corrections as AUTHORITATIVE guidance, ahead of the AI's own
    // read of the solicitation. These are global (apply to every section) and override
    // conflicting assumptions (e.g. "this is an IDIQ, not a single-award buy").
    const corrections = (collectedData.additionalContext as any)?.userCorrections;
    let effectiveGoverningRules = governingRules;
    if (Array.isArray(corrections) && corrections.length > 0) {
      const block = corrections
        .map((c: any) => `- ${typeof c === 'string' ? c : (c?.text || '')}`.trim())
        .filter((l: string) => l.length > 2)
        .join('\n');
      if (block) {
        effectiveGoverningRules = `${governingRules ? governingRules + '\n\n' : ''}===== USER CORRECTIONS (AUTHORITATIVE — these override any conflicting assumption above; apply them exactly in every section) =====\n${block}\n===== END USER CORRECTIONS =====`;
      }
    }

    // Truncate raw solicitation text - use a smaller limit to avoid payload issues.
    let rawText = collectedData.solicitationText || '';
    if (rawText.length > maxLength) {
      rawText = rawText.substring(0, maxLength) + '\n\n[... text truncated ...]';
    }

    const solicitationText = rawText;

    // Build document summary
    const docs = collectedData.contractDocuments || [];
    const documentSummary = docs.length > 0
      ? `Analyzed ${docs.length} solicitation document(s): ${docs.map(d => d.name).join(', ')}`
      : 'No solicitation documents uploaded';

    return {
      solicitationText,
      proposalPlan: collectedData.proposalPlan,
      documentSummary,
      governingRules: effectiveGoverningRules
    };
  }

  private async generateCoverLetter(
    collectedData: CollectedData,
    contract: ContractData,
    proposalDigest?: string
  ): Promise<ProposalSection> {
    // Cover letter doesn't need full solicitation text - just the plan summary
    const solicitationContext = this.buildSolicitationContext(collectedData, 5000);

    console.log('📝 Calling generate-cover-letter edge function...');

    try {
      const { data, error } = await supabase.functions.invoke('generate-cover-letter', {
        body: {
          // RAG mode: pass contract_id for vector retrieval
          contract_id: contract.id,
          use_rag: true,
          company: collectedData.company,
          contract: {
            title: contract.title,
            agency: contract.agency,
            solicitationNumber: contract.solicitationNumber,
            naicsCode: contract.naicsCode,
            dueDate: contract.dueDate
          },
          capabilities: collectedData.capabilities,
          pastPerformance: collectedData.pastPerformance,
          additionalContext: collectedData.additionalContext,
          // Pass solicitation data as fallback if RAG unavailable
          proposalPlan: solicitationContext.proposalPlan,
          documentSummary: solicitationContext.documentSummary,
          // Authoritative governing rules extracted from THIS solicitation
          governingRules: solicitationContext.governingRules,
          solicitationText: solicitationContext.solicitationText,
          // Digest of the already-drafted proposal sections so the cover letter
          // reflects the ACTUAL proposal rather than guessing from the solicitation.
          generatedSections: proposalDigest || ''
        }
      });

      if (error) {
        console.error('❌ Cover letter generation error:', error);
        throw new Error(`Cover letter generation failed: ${error.message}`);
      }

      console.log('✅ Cover letter generated successfully');
      return data;
    } catch (err: any) {
      console.error('❌ Cover letter generation exception:', err);
      throw new Error(`Cover letter generation failed: ${err.message}`);
    }
  }

  private async generateExecutiveSummary(
    collectedData: CollectedData,
    contract: ContractData,
    proposalDigest?: string
  ): Promise<ProposalSection> {
    // Executive summary needs more context but still limited
    const solicitationContext = this.buildSolicitationContext(collectedData, 15000);

    console.log('📝 Calling generate-executive-summary edge function...');

    try {
      const { data, error } = await supabase.functions.invoke('generate-executive-summary', {
        body: {
          // RAG mode: pass contract_id for vector retrieval
          contract_id: contract.id,
          use_rag: true,
          company: {
            name: collectedData.company.name,
            valueProposition: collectedData.company.valueProposition,
            yearEstablished: collectedData.company.yearEstablished,
            coreCompetencies: collectedData.company.coreCompetencies,
            differentiators: collectedData.company.differentiators,
            certificationsHeld: collectedData.company.certificationsHeld
          },
          contract: {
            title: contract.title,
            agency: contract.agency,
            naicsCode: contract.naicsCode,
            requirements: contract.requirements,
            evaluationCriteria: contract.evaluationCriteria,
            sow: contract.sow
          },
          capabilities: collectedData.capabilities,
          pastPerformance: collectedData.pastPerformance,
          personnel: collectedData.personnel,
          certifications: collectedData.certifications,
          additionalContext: collectedData.additionalContext,
          // Fallback data if RAG unavailable
          solicitationText: solicitationContext.solicitationText,
          proposalPlan: solicitationContext.proposalPlan,
          documentSummary: solicitationContext.documentSummary,
          governingRules: solicitationContext.governingRules,
          // Digest of the already-drafted proposal sections so the executive summary
          // summarizes the ACTUAL proposal rather than guessing from the solicitation.
          generatedSections: proposalDigest || ''
        }
      });

      if (error) {
        console.error('❌ Executive summary generation error:', error);
        throw new Error(`Executive summary generation failed: ${error.message}`);
      }

      console.log('✅ Executive summary generated successfully');
      return data;
    } catch (err: any) {
      console.error('❌ Executive summary generation exception:', err);
      throw new Error(`Executive summary generation failed: ${err.message}`);
    }
  }

  // Build the request body for the generate-technical-management edge function.
  private buildTechnicalManagementBody(
    collectedData: CollectedData,
    contract: ContractData,
    deliverablesData?: DeliverablesData | null
  ): Record<string, any> {
    const solicitationContext = this.buildSolicitationContext(collectedData, 20000);
    return {
      // RAG mode: pass contract_id for vector retrieval
      contract_id: contract.id,
      use_rag: true,
      company: {
        name: collectedData.company.name,
        valueProposition: collectedData.company.valueProposition,
        coreCompetencies: collectedData.company.coreCompetencies,
        keyTechnologies: collectedData.company.keyTechnologies,
        industriesServed: collectedData.company.industriesServed
      },
      contract: {
        title: contract.title,
        agency: contract.agency,
        naicsCode: contract.naicsCode,
        requirements: contract.requirements,
        sow: contract.sow
      },
      capabilities: collectedData.capabilities,
      pastPerformance: collectedData.pastPerformance,
      personnel: collectedData.personnel,
      additionalContext: collectedData.additionalContext,
      // Requirements → deliverables → labor + ODC backbone for coherent, grounded sections
      deliverablesData: deliverablesData ? {
        deliverables_with_labor: deliverablesData.deliverables_with_labor || [],
        labor_summary: deliverablesData.labor_summary,
        non_labor_summary: (deliverablesData as any).non_labor_summary || null,
        product_definition: deliverablesData.product_definition || null
      } : null,
      // Fallback data if RAG unavailable
      solicitationText: solicitationContext.solicitationText,
      proposalPlan: solicitationContext.proposalPlan,
      documentSummary: solicitationContext.documentSummary,
      governingRules: solicitationContext.governingRules,
      // PWS task tree drives the technical approach structure (no invented tasks)
      pwsTasks: collectedData.solicitationProfile?.pwsTasks || null,
      technicalConstraints: collectedData.solicitationProfile?.technicalConstraints || null
    };
  }

  /**
   * Technical & Management is the heaviest gate — at high effort it can exceed the
   * edge function's 150s request idle timeout. When a `session` is provided we run it
   * ASYNC: the edge function returns immediately and finishes generation in a
   * background task (EdgeRuntime.waitUntil, up to the Pro plan's 400s wall clock),
   * writing the result straight into the session row. We poll until it flips. Without
   * a session (legacy one-shot path) it runs synchronously and returns the result.
   */
  private async generateTechnicalManagement(
    collectedData: CollectedData,
    contract: ContractData,
    deliverablesData?: DeliverablesData | null,
    session?: GenerationSession
  ): Promise<{ technical: ProposalSection; management: ProposalSection }> {
    const body = this.buildTechnicalManagementBody(collectedData, contract, deliverablesData);

    console.log('📝 Calling generate-technical-management edge function...');
    if (deliverablesData) {
      console.log(`🔗 Grounding Technical/Management in ${deliverablesData.deliverables_with_labor?.length || 0} deliverables + labor/ODC backbone`);
    }

    // Async mode: tell the edge function which session/phase to write back to.
    if (session) {
      body.session_id = session.id;
      body.phase_id = 'technical-management';
    }

    try {
      const { data, error } = await supabase.functions.invoke('generate-technical-management', { body });

      if (error) {
        console.error('❌ Technical/management generation error:', error);
        throw new Error(`Technical/management generation failed: ${error.message}`);
      }

      // Async path: the edge accepted the job (202) and is generating in the
      // background. Poll the session row until the phase result is written.
      if (session && data?.async) {
        console.log('⏳ Technical/Management generating in background; polling for result…');
        const refreshed = await this.pollPhaseUntilDone(session.profileId, session.noticeId, 'technical-management', 7 * 60 * 1000);
        const ph = refreshed.phases['technical-management'];
        if (!ph || ph.status === 'generating') {
          throw new Error('Technical/Management generation timed out before completing.');
        }
        if (ph.status === 'error') {
          throw new Error(ph.error || 'Technical/Management generation failed.');
        }
        const payload = ph.payload || {};
        console.log('✅ Technical/management generated successfully (async)');
        return { technical: payload.technical, management: payload.management };
      }

      console.log('✅ Technical/management generated successfully');
      return data;
    } catch (err: any) {
      console.error('❌ Technical/management generation exception:', err);
      throw new Error(`Technical/management generation failed: ${err.message}`);
    }
  }

  /** Poll the session row until a phase leaves the `generating` state (or time out).
   *  Stops early if the session is no longer active (e.g. the user Abandoned it),
   *  so closing a gate mid-poll ends the background loop within one interval. */
  private async pollPhaseUntilDone(
    profileId: string,
    noticeId: string,
    phaseId: GenerationPhaseId,
    timeoutMs: number
  ): Promise<GenerationSession> {
    const start = Date.now();
    const intervalMs = 4000;
    let last: GenerationSession | null = null;
    while (Date.now() - start < timeoutMs) {
      await new Promise(r => setTimeout(r, intervalMs));
      try {
        const refreshed = await this.loadSession(profileId, noticeId);
        if (refreshed) {
          last = refreshed;
          // The user abandoned (or otherwise closed) this session while it ran —
          // stop polling rather than running out the full timeout budget.
          if (refreshed.status !== 'active') {
            throw new Error('Generation was cancelled.');
          }
          const st = refreshed.phases[phaseId]?.status;
          if (st && st !== 'generating') return refreshed;
        }
      } catch (e: any) {
        if (e?.message === 'Generation was cancelled.') throw e;
        console.warn('Poll error (will retry):', e);
      }
    }
    if (last) return last; // timed out — caller treats lingering `generating` as a failure
    throw new Error('Generation timed out (no session)');
  }

  private async generatePastPerformancePersonnel(
    collectedData: CollectedData,
    contract: ContractData
  ): Promise<{ pastPerformance: ProposalSection; personnel: ProposalSection }> {
    // Past performance needs staffing requirements from plan
    const solicitationContext = this.buildSolicitationContext(collectedData, 10000);

    console.log('📝 Calling generate-past-performance-personnel edge function...');

    try {
      const { data, error } = await supabase.functions.invoke('generate-past-performance-personnel', {
        body: {
          // RAG mode: pass contract_id for vector retrieval
          contract_id: contract.id,
          use_rag: true,
          company: {
            name: collectedData.company.name
          },
          contract: {
            title: contract.title,
            agency: contract.agency,
            naicsCode: contract.naicsCode,
            requirements: contract.requirements
          },
          pastPerformance: collectedData.pastPerformance,
          personnel: collectedData.personnel,
          additionalContext: collectedData.additionalContext,
          // Fallback data if RAG unavailable
          solicitationText: solicitationContext.solicitationText,
          proposalPlan: solicitationContext.proposalPlan,
          documentSummary: solicitationContext.documentSummary,
          governingRules: solicitationContext.governingRules,
          // Required roles + past-performance mechanics (refs, recency, PPQ) from the solicitation
          keyPersonnel: collectedData.solicitationProfile?.keyPersonnel || null,
          pastPerformanceRules: collectedData.solicitationProfile?.pastPerformance || null
        }
      });

      if (error) {
        console.error('❌ Past performance/personnel generation error:', error);
        throw new Error(`Past performance/personnel generation failed: ${error.message}`);
      }

      console.log('✅ Past performance/personnel generated successfully');
      return data;
    } catch (err: any) {
      console.error('❌ Past performance/personnel generation exception:', err);
      throw new Error(`Past performance/personnel generation failed: ${err.message}`);
    }
  }

  // Normalize the durable user corrections (stored on the session at
  // additionalContext.userCorrections) into a plain string[] for edge prompts.
  private correctionTexts(collectedData: CollectedData): string[] {
    const raw = (collectedData.additionalContext as any)?.userCorrections;
    if (!Array.isArray(raw)) return [];
    return raw
      .map((c: any) => (typeof c === 'string' ? c : (c?.text || '')).trim())
      .filter((t: string) => t.length > 2);
  }

  // Build the government price/CLIN schedule ONCE and cache it on collectedData.
  // It is the single source of truth for BOTH the deliverables backbone (one
  // deliverable per line item) and the pricing CLIN schedule
  // (profile.pricing.clins). Format/location-agnostic (content-based, never keyed
  // off a filename); when the solicitation has no schedule it returns empty and
  // the existing RAG/derive paths take over. Best-effort: never throws.
  private async ensurePriceSchedule(
    collectedData: CollectedData,
    contract: ContractData
  ): Promise<any> {
    const cached = (collectedData as any).priceSchedule;
    if (cached) return cached;

    const profile = collectedData.solicitationProfile;
    const periods = (profile?.vehicle?.periods || [])
      .map(p => p?.label)
      .filter((l): l is string => !!l);

    let schedule: any = { hasSolicitationSchedule: false, provenance: 'offeror-derived', lineItems: [] };
    try {
      const { data, error } = await supabase.functions.invoke('structure-price-schedule', {
        body: {
          contract_id: contract.id,
          solicitation_text: collectedData.solicitationText || '',
          pricing_hint: {
            instrument: profile?.pricing?.instrument || null,
            source: profile?.pricing?.source || null,
          },
          periods,
        }
      });
      if (!error && data) schedule = data;
      else if (error) console.warn('⚠️ structure-price-schedule warning:', error);
    } catch (e) {
      console.warn('⚠️ structure-price-schedule exception (continuing):', e);
    }

    (collectedData as any).priceSchedule = schedule;

    // Seed the pricing CLIN schedule from the structurer so the pricing section
    // renders a traceable, provenance-labeled schedule (single source of truth).
    if (schedule?.lineItems?.length && profile) {
      const seededClins = schedule.lineItems.map((li: any) => ({
        clin: li.clin ?? null,
        code: li.code ?? null,
        description: li.description ?? null,
        unit: li.unit ?? null,
        quantity: li.quantity ?? null,
        quantityNote: li.quantityNote ?? null,
        periods: (li.periods && li.periods.length) ? li.periods : (periods.length ? periods : undefined),
        provenance: li.provenance || schedule.provenance || null,
      }));
      profile.pricing = { ...(profile.pricing || {}), clins: seededClins };
      console.log(`📋 Seeded profile.pricing.clins with ${seededClins.length} line items (${schedule.provenance})`);
    }

    return schedule;
  }

  private async generateDeliverablesAnalysis(
    collectedData: CollectedData,
    contract: ContractData
  ): Promise<DeliverablesData | null> {
    console.log('📦 Starting deliverables analysis...');

    const solicitationText = collectedData.solicitationText || '';
    // Authoritative user corrections (e.g. "this is an IDIQ, not a single-award buy")
    // threaded into every backbone step so a backbone-gate correction reaches the
    // pipeline that doesn't render governingRules.
    const userCorrections = this.correctionTexts(collectedData);

    // Structure the price/CLIN schedule first — feeds BOTH this extraction
    // (schedule-primary: one deliverable per line) and pricing (seeded clins).
    const priceSchedule = await this.ensurePriceSchedule(collectedData, contract);

    // Step 1: Extract deliverables from RFQ (with RAG retrieval for full context)
    console.log('📄 Step 1: Extracting deliverables from RFQ...');
    const { data: extractedData, error: extractError } = await supabase.functions.invoke('extract-deliverables-raw', {
      body: {
        // RAG mode: pass contract_id for vector retrieval of relevant chunks
        contract_id: contract.id,
        use_rag: true,
        // Fallback: pass truncated text in case RAG unavailable
        rfq_text: solicitationText,
        rfq_attachments: [],
        // Structured schedule -> schedule-primary mode (one deliverable per line)
        price_schedule: priceSchedule,
        contract_metadata: {
          notice_id: contract.id,
          title: contract.title,
          contract_type: contract.contractType || 'service',
          naics_code: contract.naicsCode || '',
          agency: contract.agency,
          estimated_value: contract.fundingAmount || 0
        },
        // Fix A: thread the solicitation's authoritative scope so schedule-primary
        // deliverables are enriched (not collapsed) with real technical grounding —
        // PWS tasks, technical constraints, and the contract vehicle (type/periods/band).
        pws_tasks: collectedData.solicitationProfile?.pwsTasks || null,
        technical_constraints: collectedData.solicitationProfile?.technicalConstraints || null,
        vehicle: collectedData.solicitationProfile?.vehicle || null,
        user_corrections: userCorrections
      }
    });

    if (extractError) {
      console.warn('⚠️ Deliverables extraction warning:', extractError);
      return null;
    }

    if (!extractedData?.deliverables?.length) {
      console.log('⚠️ No deliverables found in RFQ');
      return null;
    }

    console.log(`✅ Extracted ${extractedData.deliverables.length} deliverables`);

    // Step 2: Enrich with compliance requirements
    console.log('📋 Step 2: Enriching with compliance requirements...');
    let enrichedData = extractedData;
    try {
      const { data: enriched, error: enrichError } = await supabase.functions.invoke('enrich-deliverables-compliance', {
        body: {
          extracted_deliverables: extractedData,
          contract_metadata: {
            notice_id: contract.id,
            title: contract.title,
            contract_type: contract.contractType || 'service',
            naics_code: contract.naicsCode || '',
            agency: contract.agency,
            estimated_value: contract.fundingAmount || 0
          },
          far_clauses: [],
          agency_requirements: null,
          user_corrections: userCorrections
        }
      });

      if (!enrichError && enriched) {
        // No-collapse floor guard (Fix C): enrichment must NEVER reduce the
        // deliverable count below what was extracted from the solicitation. If it
        // returns fewer lines, keep the full extracted set and only adopt the
        // enrichment's compliance data — so a 40-line schedule can't silently
        // shrink to a handful of coarse items.
        const extractedCount = extractedData.deliverables?.length || 0;
        const enrichedCount = enriched.deliverables?.length || 0;
        if (enrichedCount < extractedCount) {
          console.warn(`⚠️ Enrichment collapsed deliverables (${extractedCount} → ${enrichedCount}); restoring full extracted set, keeping enrichment compliance data`);
          enrichedData = {
            ...enriched,
            deliverables: extractedData.deliverables,
            compliance_requirements: enriched.compliance_requirements || extractedData.compliance_requirements,
            compliance_framework: enriched.compliance_framework || extractedData.compliance_framework,
          };
        } else {
          enrichedData = enriched;
        }
        console.log(`✅ Compliance enrichment complete (${enrichedData.deliverables?.length || 0} deliverables)`);
      }
    } catch (enrichErr) {
      console.warn('⚠️ Compliance enrichment warning (continuing):', enrichErr);
    }

    // Step 3: Generate labor framework
    console.log('💼 Step 3: Generating labor framework...');
    // Fix B: ground the labor estimate in the real contract vehicle — number of
    // performance periods, total months, vehicle type, and the min/max value band —
    // so recurring effort scales across all periods (not a hardcoded single 12 months).
    const vProfile = collectedData.solicitationProfile?.vehicle;
    const vPeriods = (vProfile?.periods || []).filter(p => p && (p.label || p.months));
    const numberOfPeriods = vPeriods.length || 1;
    const totalMonths = vPeriods.length
      ? vPeriods.reduce((sum, p) => sum + (Number(p.months) || 12), 0)
      : 12;
    const vehicleType = vProfile?.type || contract.contractType || null;
    const isIdiq = /\b(idiq|indefinite[\s-]*delivery|indefinite[\s-]*quantity|task[\s-]*order|bpa|delivery[\s-]*order)\b/i
      .test(`${vehicleType || ''} ${collectedData.solicitationProfile?.pricing?.instrument || ''}`);
    const { data: laborFramework, error: laborError } = await supabase.functions.invoke('generate-labor-framework', {
      body: {
        enriched_deliverables: enrichedData,
        user_profile: {
          labor_categories: collectedData.pricing?.labor?.map(l => ({
            title: l.roleTitle,
            rate: l.hourlyRate
          })),
          past_performance: collectedData.pastPerformance?.map(pp => ({
            project_type: pp.projectName,
            hours: 0 // We don't have hours data in past performance
          }))
        },
        contract_constraints: {
          hours_ceiling: contract.fundingAmount ? Math.round(contract.fundingAmount / 100) : null,
          budget_ceiling: contract.fundingAmount || null,
          period_months: totalMonths,
          number_of_periods: numberOfPeriods,
          vehicle_type: vehicleType,
          far_part: vProfile?.farPart || null,
          is_idiq: isIdiq,
          guaranteed_minimum: vProfile?.guaranteedMinimum || null,
          maximum_value: vProfile?.maximumValue || null,
          periods: vPeriods.map(p => ({ label: p.label || null, months: Number(p.months) || null }))
        }
      }
    });

    if (laborError) {
      console.warn('⚠️ Labor framework generation warning:', laborError);
      return null;
    }

    console.log('✅ Labor framework generated');

    // Step 4: Synthesize product definition
    console.log('🎯 Step 4: Synthesizing product definition...');
    let productDefinition = null;
    try {
      const { data: synthesized, error: synthesizeError } = await supabase.functions.invoke('synthesize-product', {
        body: {
          deliverables: laborFramework.deliverables_with_labor?.map((d: any) => ({
            id: d.deliverable_id,
            name: d.deliverable_name,
            description: d.description,
            type: d.type,
            category: d.category,
            quantity: d.quantity,
            unit: d.unit,
            specifications: d.specifications
          })) || extractedData.deliverables,
          compliance_requirements: enrichedData.compliance_requirements || extractedData.compliance_requirements,
          labor_summary: laborFramework.labor_summary,
          contract_metadata: {
            notice_id: contract.id,
            title: contract.title,
            naics_code: contract.naicsCode || '',
            naics_description: '',
            agency: contract.agency,
            contract_type: contract.contractType || 'service',
            estimated_value: contract.fundingAmount || 0,
            period_of_performance: extractedData.timeline_summary?.period_of_performance,
            set_aside_type: contract.setAsides?.[0] || null
          },
          document_type: extractedData.document_type,
          rfq_summary: solicitationText?.substring(0, 2000),
          user_corrections: userCorrections
        }
      });

      // The edge function returns { product_definition: {...} }; tolerate both the
      // wrapped and (legacy) unwrapped shapes so the synthesized product is actually captured.
      const synthProduct = synthesized?.product_definition ?? synthesized;
      if (!synthesizeError && synthProduct && synthProduct.product_name) {
        productDefinition = synthProduct;
        console.log(`✅ Product synthesized: "${synthProduct.product_name}"`);
        console.log(`📊 Bid recommendation: ${synthProduct.bid_recommendation?.recommendation}`);
      } else if (synthesizeError) {
        console.warn('⚠️ Product synthesis warning:', synthesizeError);
      }
    } catch (synthErr) {
      console.warn('⚠️ Product synthesis error (continuing):', synthErr);
    }

    // Step 5: Derive AI market-comp labor rates + clarifying questions
    console.log('💲 Step 5: Deriving AI labor rates...');
    const aiPricing = await this.deriveLaborRates(collectedData, contract, laborFramework.labor_summary);

    // Build deliverables data
    const deliverablesData: DeliverablesData = {
      deliverables_with_labor: laborFramework.deliverables_with_labor || [],
      labor_summary: laborFramework.labor_summary || { by_category: [], total_hours: { min: 0, max: 0, recommended: 0 } },
      pricing_inputs: laborFramework.pricing_inputs || { labor_categories_required: [], hours_matrix: {}, suggested_rate_basis: 'market', sca_wage_determination_needed: false },
      non_labor_summary: laborFramework.non_labor_summary || null,
      compliance_framework: laborFramework.compliance_framework || enrichedData.compliance_framework,
      timeline_summary: extractedData.timeline_summary,
      volume_summary: extractedData.volume_summary,
      product_definition: productDefinition,
      ai_pricing: aiPricing,
      metadata: {
        generatedAt: new Date().toISOString(),
        source: 'orchestrated',
        deliverablesCount: laborFramework.deliverables_with_labor?.length || 0
      }
    };

    // Scope sanity check (Fix D): surface — never silently pad — when the final
    // deliverable set falls well short of the priced schedule (signals a downstream
    // collapse) or when total effort looks implausibly low for the contract band.
    try {
      const finalCount = deliverablesData.deliverables_with_labor.length;
      const scheduleCount = (priceSchedule?.lineItems?.length) || 0;
      if (scheduleCount > 0 && finalCount < scheduleCount) {
        console.warn(`⚠️ Scope sanity: ${finalCount} priced deliverables vs ${scheduleCount} schedule line items — downstream step may have dropped scope.`);
      }
      const recHours = deliverablesData.labor_summary?.total_hours?.recommended || 0;
      const minBand = this.parseCurrency(collectedData.solicitationProfile?.vehicle?.guaranteedMinimum);
      if (minBand && recHours > 0 && recHours < 1000 && minBand > 500000) {
        console.warn(`⚠️ Scope sanity: ${recHours} recommended labor hours look low for a >$${Math.round(minBand / 1000)}K floor — pricing floor reconciliation will flag any below-minimum total.`);
      }
    } catch { /* advisory only */ }

    return deliverablesData;
  }

  // Parse a currency-ish string ("$525,000", "525000", "$2M") to a number, or null.
  private parseCurrency(v?: string | null): number | null {
    if (!v) return null;
    const s = String(v).trim().toLowerCase().replace(/[$,\s]/g, '');
    const m = s.match(/^([\d.]+)\s*(k|m|b)?/);
    if (!m) return null;
    let n = parseFloat(m[1]);
    if (!Number.isFinite(n)) return null;
    if (m[2] === 'k') n *= 1e3;
    else if (m[2] === 'm') n *= 1e6;
    else if (m[2] === 'b') n *= 1e9;
    return n;
  }

  /**
   * Derive market-comp labor rates and clarifying questions via the
   * derive-labor-rates edge function. Falls back to null on any failure so
   * the rest of pricing can proceed with profile/static rates.
   */
  private async deriveLaborRates(
    collectedData: CollectedData,
    contract: ContractData,
    laborSummary: LaborSummary | null
  ): Promise<AiPricing | null> {
    const byCategory = laborSummary?.by_category || [];
    if (byCategory.length === 0) {
      console.log('⚠️ No labor categories to price - skipping AI rate derivation');
      return null;
    }

    const labor_categories = byCategory.map(c => ({
      category: c.category,
      sca_title: c.sca_title,
      total_hours: c.total_hours
    }));

    // Surface the company's grounded labor categories so derive-labor-rates
    // resolves each proposal role against THEM first (exact → comparable by
    // SCA/SOC/functional family) and uses the real base rate + SOC + key flag,
    // instead of pricing off the BLS card or inventing a rate.
    const known_rates = (collectedData.pricing?.labor || []).map(l => ({
      category: l.roleTitle || l.laborCategory,
      base_hourly: l.hourlyRate,
      loaded_hourly: l.loadedRate,
      sca_title: l.scaTitle ?? null,
      soc_code: l.socCode ?? null,
      functional_area: l.functionalArea ?? null,
      is_key_personnel: l.isKeyPersonnel ?? false,
    }));

    // Any pricing answers the user supplied through gap resolution
    const user_answers = (collectedData.additionalContext as any)?.pricingAnswers || {};

    const place = contract.placeOfPerformance;
    const placeOfPerformance = [place?.city, place?.state, place?.country]
      .filter(Boolean).join(', ') || null;

    // Company default work locality (Costing Studio). The edge fn resolves:
    // per-proposal answer > solicitation place of performance > this default.
    const laborLocality = await this.loadWorkLocality();

    // Canonical published rate card (BLS OEWS). The edge fn resolves each labor
    // category against this DETERMINISTICALLY and only falls back to the LLM for
    // categories with no published match — so a fully-matched proposal costs zero
    // API tokens for rate derivation.
    const published_rates = await this.loadPublishedRates();

    // Parse the solicitation's attached SCA Wage Determination (deterministic, no LLM).
    // Covered service categories get floored at the WD minimum in derive-labor-rates.
    let sca_wage_determination: any = null;
    try {
      const { data: wd } = await supabase.functions.invoke('parse-wage-determination', {
        body: { contract_id: contract.id, persist: false },
      });
      if (wd?.found && Array.isArray(wd.determinations) && wd.determinations.length) {
        const occupations = wd.determinations.flatMap((d: any) => d.occupations || []);
        const hw_rate = Math.max(0, ...wd.determinations.map((d: any) => Number(d.hw_rate) || 0)) || null;
        sca_wage_determination = { wd_number: wd.determinations[0]?.wd_number || null, hw_rate, occupations };
        console.log(`⚖️ SCA WD ${sca_wage_determination.wd_number}: ${occupations.length} covered occupations, H&W $${hw_rate}/hr`);
      }
    } catch (e) {
      console.warn('⚠️ SCA WD parse failed (continuing without floor):', e);
    }

    try {
      const { data, error } = await supabase.functions.invoke('derive-labor-rates', {
        body: {
          published_rates,
          sca_wage_determination,
          contract: {
            title: contract.title,
            agency: contract.agency,
            naics_code: contract.naicsCode,
            contract_type: contract.contractType,
            place_of_performance: placeOfPerformance,
            period_of_performance: contract.periodOfPerformance,
            estimated_value: contract.fundingAmount
          },
          labor_categories,
          profile: {
            company_name: collectedData.company.name,
            size_standard: collectedData.setAsides?.length ? 'small business' : undefined,
            location: collectedData.company.address?.full,
            labor_locality: laborLocality,
            certifications: collectedData.certifications,
            set_asides: collectedData.setAsides,
            known_rates
          },
          user_answers,
          // Indirect rates the company already configured (Costing Studio / profile). The
          // edge fn treats these as known so it doesn't ASK the user to re-confirm them.
          configured_rates: {
            fringe_rate: (collectedData.pricing as any)?.fringeRate ?? null,
            overhead_rate: (collectedData.pricing as any)?.overheadPercentage ?? null,
            ga_rate: (collectedData.pricing as any)?.gaRate ?? null,
            fee_rate: (collectedData.pricing as any)?.profitMargin ?? null,
          },
          user_corrections: this.correctionTexts(collectedData)
        }
      });

      if (error) {
        console.warn('⚠️ AI rate derivation failed (continuing):', error);
        return null;
      }
      console.log(`✅ AI rates derived: ${data?.cost_rollup?.by_category?.length || 0} categories, total $${data?.cost_rollup?.total_price?.toLocaleString?.() || 0}, ${data?.clarifying_questions?.length || 0} questions`);
      return data as AiPricing;
    } catch (e) {
      console.warn('⚠️ AI rate derivation exception (continuing):', e);
      return null;
    }
  }

  /**
   * Build the pricing payload for generate-pricing-compliance. Profile rates
   * win when present; otherwise we map the AI-derived rates + indirect
   * assumptions into the same shape so the existing pricing math uses them.
   */
  private buildPricingInput(
    profilePricing: CollectedData['pricing'],
    aiPricing?: AiPricing | null
  ): CollectedData['pricing'] {
    const base = profilePricing || {
      labor: [], materials: [], equipment: [], fuel: [], lease: [], overhead: []
    };

    // Keep the user's own rates if they exist.
    if (base.labor && base.labor.length > 0) return base;
    if (!aiPricing) return base;

    const labor = (aiPricing.cost_rollup?.by_category || []).map(c => ({
      roleTitle: c.category,
      laborCategory: c.sca_title || c.category,
      hourlyRate: c.base_hourly,
      loadedRate: c.loaded_hourly
    }));

    const ind = aiPricing.indirect_assumptions || {} as AiPricing['indirect_assumptions'];
    const overhead = [
      { description: 'Fringe', rate: ind.fringe_pct ?? 0, costType: 'percent' },
      { description: 'Overhead', rate: ind.overhead_pct ?? 0, costType: 'percent' },
      { description: 'G&A', rate: ind.ga_pct ?? 0, costType: 'percent' },
      { description: 'Fee/Profit', rate: ind.fee_pct ?? 0, costType: 'percent' }
    ];

    return {
      ...base,
      labor,
      overhead: (base.overhead && base.overhead.length > 0) ? base.overhead : overhead
    };
  }

  private async generatePricingCompliance(
    collectedData: CollectedData,
    contract: ContractData,
    deliverablesData?: DeliverablesData | null
  ): Promise<{ pricing: ProposalSection; compliance: ProposalSection }> {
    // Pricing needs compliance requirements from plan
    const solicitationContext = this.buildSolicitationContext(collectedData, 15000);

    console.log('📝 Calling generate-pricing-compliance edge function...');
    if (deliverablesData) {
      console.log(`📊 Including ${deliverablesData.deliverables_with_labor?.length || 0} deliverables with labor framework`);
    }

    // Prefer the user's own profile rates; otherwise inject the AI-derived
    // market-comp rates so generate-pricing-compliance prices with real
    // numbers instead of falling back to its static estimate table.
    const pricingInput = this.buildPricingInput(collectedData.pricing, deliverablesData?.ai_pricing);

    // Derive the REAL pricing structure from the solicitation profile so pricing stops
    // defaulting to "Base + 2 option years / FAR Table 15-2 cost buildup". For a
    // commercial FAR 12/13 buy we must emit the CLIN price schedule, not a cost narrative.
    const profile = collectedData.solicitationProfile;
    const profilePeriods = (profile?.vehicle?.periods || []).map(p => p.label).filter(Boolean);
    const isCommercial = profile?.vehicle?.isCommercialItem === true ||
      ['12', '13'].includes(String(profile?.vehicle?.farPart || ''));
    const costBuildupProhibited = profile?.pricing?.costBuildupProhibited === true || isCommercial;
    const effectiveContractType = contract.contractType || (isCommercial ? 'FFP' : null);

    // Locality inputs for the SCA wage-determination block (stops the
    // [LOCALITY]/[WD NUMBER] placeholders): solicitation place of performance
    // plus the company's Costing Studio work locality as fallback.
    const pcPlace = contract.placeOfPerformance;
    const pcPlaceOfPerformance = [pcPlace?.city, pcPlace?.state, pcPlace?.country]
      .filter(Boolean).join(', ') || null;
    const pcWorkLocality = await this.loadWorkLocality();

    try {
      const { data, error } = await supabase.functions.invoke('generate-pricing-compliance', {
        body: {
          // RAG mode: pass contract_id for vector retrieval
          contract_id: contract.id,
          use_rag: true,
          company: {
            name: collectedData.company.name,
            duns: collectedData.company.duns,
            cage: collectedData.company.cage,
            samStatus: collectedData.company.samStatus,
            // Plumb the rest of the cover-sheet identity so the FAR Table 15-2 cover
            // page renders real values instead of [COMPANY ADDRESS]/[POC NAME]/[UEI].
            uei: collectedData.company.uei,
            address: collectedData.company.address?.full || null,
            email: collectedData.company.email,
            phone: collectedData.company.phone,
            pocName: collectedData.company.fullName,
            pocTitle: collectedData.company.title,
            workLocality: pcWorkLocality
          },
          contract: {
            title: contract.title,
            agency: contract.agency,
            naicsCode: contract.naicsCode,
            fundingAmount: contract.fundingAmount,
            periodOfPerformance: contract.periodOfPerformance,
            contractType: effectiveContractType,
            setAsides: contract.setAsides,
            requirements: contract.requirements,
            // Real solicitation/RFQ number for the cover sheet (was being regex-scraped
            // from the title, which fails on titles like "U008--FY26 VHA OMH...").
            solicitationNumber: contract.solicitationNumber,
            placeOfPerformance: pcPlaceOfPerformance
          },
          pricing: pricingInput,
          // Surface AI rate derivation so the section can cite basis + show questions
          aiPricing: deliverablesData?.ai_pricing || null,
          certifications: collectedData.certifications,
          setAsides: collectedData.setAsides,
          securityClearance: collectedData.securityClearance,
          additionalContext: collectedData.additionalContext,
          // Solicitation-driven pricing structure (overrides hardcoded defaults)
          governingRules: solicitationContext.governingRules,
          pricingStructure: profile ? {
            vehicleType: profile.vehicle?.type || null,
            farPart: profile.vehicle?.farPart || null,
            isCommercialItem: profile.vehicle?.isCommercialItem ?? null,
            costBuildupProhibited,
            guaranteedMinimum: profile.vehicle?.guaranteedMinimum || null,
            maximumValue: profile.vehicle?.maximumValue || null,
            periods: profilePeriods.length > 0 ? profilePeriods : null,
            instrument: profile.pricing?.instrument || null,
            source: profile.pricing?.source || null,
            clins: profile.pricing?.clins || null
          } : null,
          // Fallback data if RAG unavailable
          solicitationText: solicitationContext.solicitationText,
          proposalPlan: solicitationContext.proposalPlan,
          documentSummary: solicitationContext.documentSummary,
          // Include FULL deliverables data for informed pricing
          // This data drives: labor categories, hours, equipment, CLINs
          deliverablesData: deliverablesData ? {
            deliverables_with_labor: deliverablesData.deliverables_with_labor || [],
            labor_summary: deliverablesData.labor_summary,
            pricing_inputs: deliverablesData.pricing_inputs,
            non_labor_summary: deliverablesData.non_labor_summary || null,
            deliverables_count: deliverablesData.deliverables_with_labor?.length || 0,
            product_definition: deliverablesData.product_definition || null
          } : null
        }
      });

      if (error) {
        console.error('❌ Pricing/compliance generation error:', error);
        throw new Error(`Pricing/compliance generation failed: ${error.message}`);
      }

      console.log('✅ Pricing/compliance generated successfully');
      return data;
    } catch (err: any) {
      console.error('❌ Pricing/compliance generation exception:', err);
      throw new Error(`Pricing/compliance generation failed: ${err.message}`);
    }
  }

  private async generateAIFeedback(
    collectedData: CollectedData,
    contract: ContractData,
    proposal: GeneratedProposal,
    acknowledgedGaps: string[] = []
  ): Promise<AIFeedback> {
    // Extract content from proposal sections
    const proposalContent: Record<string, string> = {};

    if (proposal.coverLetter?.content) {
      proposalContent.coverLetter = proposal.coverLetter.content;
    }
    if (proposal.executiveSummary?.content) {
      proposalContent.executiveSummary = proposal.executiveSummary.content;
    }
    if (proposal.technical?.content) {
      proposalContent.technical = proposal.technical.content;
    }
    if (proposal.management?.content) {
      proposalContent.management = proposal.management.content;
    }
    if (proposal.pastPerformance?.content) {
      proposalContent.pastPerformance = proposal.pastPerformance.content;
    }
    if (proposal.personnel?.content) {
      proposalContent.personnel = proposal.personnel.content;
    }
    if (proposal.pricing?.content) {
      proposalContent.pricing = proposal.pricing.content;
    }
    if (proposal.compliance?.content) {
      proposalContent.compliance = proposal.compliance.content;
    }

    // Reconstruct gaps from acknowledged gaps (simplified)
    const gaps = acknowledgedGaps.map(field => ({
      field,
      message: `Gap in ${field}`,
      section: field.split('.')[0] || 'general',
      severity: 'recommended'
    }));

    const { data, error } = await supabase.functions.invoke('generate-ai-feedback', {
      body: {
        contract: {
          title: contract.title,
          agency: contract.agency,
          naicsCode: contract.naicsCode,
          requirements: contract.requirements,
          evaluationCriteria: contract.evaluationCriteria,
          setAsides: contract.setAsides
        },
        proposal: proposalContent,
        companyProfile: {
          name: collectedData.company.name,
          certifications: collectedData.certifications,
          setAsides: collectedData.setAsides,
          pastPerformanceCount: collectedData.pastPerformance.length,
          personnelCount: collectedData.personnel.length
        },
        gaps
      }
    });

    if (error) throw new Error(`AI feedback generation failed: ${error.message}`);
    return data as AIFeedback;
  }

  // ============================================================================
  // CHECKPOINT-GATED GENERATION API (human-in-the-loop, resumable)
  //
  // Additive to the legacy one-shot generateProposal() above (which is left
  // untouched and used when VITE_CHECKPOINT_GENERATION is off). Each runPhase()
  // generates exactly ONE gate, persists the result + any AI questions to the
  // proposal_generation_sessions table, and STOPS — it never auto-advances.
  // All inputs for a phase are reconstructed from DB-persisted approved phases,
  // so a session can be closed and resumed in a later browser session.
  // ============================================================================

  private static SESSIONS_TABLE = 'proposal_generation_sessions';

  // Map a DB row to the in-memory session shape (contract is hydrated from the
  // G0 phase payload, where it is persisted).
  private sessionFromRow(row: any): GenerationSession {
    const phases = (row.phases || {}) as Record<string, GenerationPhaseState>;
    const g0 = (phases['gap-resolution'] || {}) as any;
    return {
      id: row.id,
      profileId: row.profile_id,
      noticeId: row.notice_id,
      contractTitle: row.contract_title,
      status: row.status,
      currentPhase: row.current_phase,
      collectedData: row.collected_data,
      solicitationProfile: row.solicitation_profile ?? null,
      contract: g0.payload?.contract,
      phases,
      questions: (row.ai_questions || []) as GenerationQuestion[],
      proposalTemplateId: row.proposal_template_id ?? null,
    };
  }

  private async persistSession(session: GenerationSession): Promise<void> {
    const { error } = await supabase
      .from(OrchestratedProposalService.SESSIONS_TABLE)
      .update({
        status: session.status,
        current_phase: session.currentPhase,
        collected_data: session.collectedData,
        solicitation_profile: session.solicitationProfile,
        phases: session.phases,
        ai_questions: session.questions,
        proposal_template_id: session.proposalTemplateId ?? null,
      })
      .eq('id', session.id);
    if (error) throw new Error(`Failed to persist generation session: ${error.message}`);
  }

  /** Load the active/most-recent session for a contract, or null. */
  async loadSession(profileId: string, noticeId: string): Promise<GenerationSession | null> {
    const { data, error } = await supabase
      .from(OrchestratedProposalService.SESSIONS_TABLE)
      .select('*')
      .eq('profile_id', profileId)
      .eq('notice_id', noticeId)
      .maybeSingle();
    if (error) throw new Error(`Failed to load generation session: ${error.message}`);
    return data ? this.sessionFromRow(data) : null;
  }

  /** Sessions the user can resume (for the dashboard "Resume generation" chip). */
  async listResumableSessions(profileId: string): Promise<Array<{
    id: string; noticeId: string; contractTitle: string | null;
    currentPhase: string; updatedAt: string;
  }>> {
    const { data, error } = await supabase
      .from(OrchestratedProposalService.SESSIONS_TABLE)
      .select('id, notice_id, contract_title, current_phase, updated_at')
      .eq('profile_id', profileId)
      .eq('status', 'active')
      .order('updated_at', { ascending: false });
    if (error) throw new Error(`Failed to list generation sessions: ${error.message}`);
    return (data || []).map((r: any) => ({
      id: r.id, noticeId: r.notice_id, contractTitle: r.contract_title,
      currentPhase: r.current_phase, updatedAt: r.updated_at,
    }));
  }

  // =========================================================================
  // RED TEAM — manual, re-runnable adversarial submitability review.
  // Runs review-submitability over the FULL assembled proposal + the canonical
  // solicitation truth, persists the verdict (latest+prior) on content_json, and
  // maps it onto the existing Gap Report / Win Checklist shapes.
  // =========================================================================

  /** Project a Red Team verdict onto the existing Gap Report / Win Checklist views. */
  private mapVerdictToViews(v: SubmitabilityVerdict): {
    gapReport: GapReport; winAssessment: WinAssessment; improvementRoadmap: ImprovementRoadmap;
  } {
    const impactFor = (sev: string) =>
      sev === 'fatal' ? 'CRITICAL - May result in disqualification'
      : sev === 'major' ? 'HIGH - Hurts evaluation score' : 'LOW - Cleanup';

    const criticalGaps = [...v.fatalBlockers, ...v.humanRequired].map((b) => ({
      requirement: b.title,
      requirementSource: b.section || b.category || 'Red Team review',
      impact: impactFor(b.severity),
      recommendation: b.recommendedAction || b.detail || '',
      profileAction: b.recommendedAction || '',
    }));
    const improvementOpportunities = v.majorRisks.map((r) => ({
      requirement: r.title,
      currentState: r.detail || '',
      recommendation: r.recommendedAction || '',
      impactOnWinProbability: 'Addressing this strengthens the evaluation.',
      profileAction: r.recommendedAction || '',
    }));
    const strengthsToHighlight = (v.strengths || []).map((s, i) => ({
      evidenceId: `RT-${i + 1}`, strength: s, suggestedUsage: 'Emphasize in the proposal',
      relevance: 'HIGH' as const,
    }));
    const competitivePosition: GapReport['competitivePosition'] =
      v.fatalBlockers.length ? 'NOT_COMPETITIVE'
      : v.submitabilityScore >= 75 ? 'STRONG'
      : v.submitabilityScore >= 50 ? 'MODERATE' : 'WEAK';
    const cm = v.complianceMatrix || [];
    const gapReport: GapReport = {
      overallReadiness: v.submitabilityScore,
      winProbabilityEstimate: v.submitabilityScore,
      competitivePosition,
      criticalGaps,
      improvementOpportunities,
      strengthsToHighlight,
      profileCompleteness: [],
      summary: {
        totalRequirements: cm.length,
        fullyMet: cm.filter((c) => c.status === 'met').length,
        partiallyMet: cm.filter((c) => c.status === 'at-risk').length,
        notMet: cm.filter((c) => c.status === 'missing').length,
        criticalGapsCount: v.fatalBlockers.length,
        recommendationsCount: improvementOpportunities.length,
      },
      complianceMatrix: cm,
    };
    const winAssessment: WinAssessment = {
      overallReadiness: v.submitabilityScore,
      winProbabilityEstimate: v.submitabilityScore,
      strengthAreas: strengthsToHighlight.map((s) => s.strength).slice(0, 6),
      weaknessAreas: v.majorRisks.map((r) => r.title).slice(0, 6),
      competitivePosition,
      factors: {
        requirementCoverage: cm.length ? Math.round(100 * cm.filter((c) => c.status === 'met').length / cm.length) : v.submitabilityScore,
        evidenceStrength: v.submitabilityScore,
        relevanceAlignment: v.submitabilityScore,
        gapSeverity: Math.max(0, 100 - v.fatalBlockers.length * 20 - v.majorRisks.length * 5),
      },
      submitabilityScore: v.submitabilityScore,
      recommendation: v.recommendation,
    };
    const toAction = (issue: any, effort: 'LOW' | 'MEDIUM' | 'HIGH') => ({
      action: issue.title + (issue.recommendedAction ? ` — ${issue.recommendedAction}` : ''),
      impact: issue.detail || '',
      effort,
      proposalSection: issue.section,
    });
    const improvementRoadmap: ImprovementRoadmap = {
      criticalForCompliance: v.fatalBlockers.map((b) => toAction(b, 'HIGH')),
      highImpactOnWin: v.majorRisks.map((r) => toAction(r, 'MEDIUM')),
      differentiators: (v.autoFixable || []).map((a) => toAction(a, 'LOW')),
    };
    return { gapReport, winAssessment, improvementRoadmap };
  }

  /**
   * Run the Red Team reviewer over a finished proposal and persist the verdict.
   * Manual + re-runnable: passes the prior verdict for a delta and stores
   * { current, prior } on content_json.redTeamAssessment.
   */
  async runSubmitabilityReview(params: {
    proposalTemplateId: string;
    profileId: string;
    noticeId: string;
    contentJson: any;
    contract?: any;
  }): Promise<{ verdict: SubmitabilityVerdict; contentJson: any }> {
    const { proposalTemplateId, profileId, noticeId, contentJson, contract } = params;

    // 1) Assemble the proposal sections from content_json (orchestrated shape).
    const ts: Array<{ title: string; content: string }> = Array.isArray(contentJson?.technicalSections)
      ? contentJson.technicalSections : [];
    const pick = (needle: string) =>
      (ts.find((s) => (s.title || '').toLowerCase().includes(needle))?.content || '');
    const proposal = {
      coverLetter: contentJson?.coverLetter || '',
      executiveSummary: contentJson?.executiveSummary || pick('executive'),
      technicalApproach: contentJson?.technicalApproach || pick('technical'),
      managementApproach: contentJson?.managementApproach || pick('management'),
      pastPerformance: contentJson?.pastPerformance || pick('past performance'),
      personnel: contentJson?.personnel || pick('personnel'),
      pricingStrategy: contentJson?.pricingStrategy || pick('pricing'),
      compliance: contentJson?.compliance || pick('compliance'),
    };

    // 2) Canonical solicitation truth from the generation session (best-effort).
    let profileObj: any = null;
    let solicitationText = '';
    try {
      const session = await this.loadSession(profileId, noticeId);
      profileObj = session?.solicitationProfile || null;
      solicitationText = (session?.collectedData as any)?.solicitationText || '';
    } catch { /* no session — fall back to contract + deliverables data */ }
    const clins = profileObj?.pricing?.clins || [];
    const dd = contentJson?.deliverablesData || {};
    const complianceRequirements =
      dd?.compliance_requirements || dd?.enriched_deliverables?.compliance_requirements || [];

    // 3) Prior verdict (for the delta on re-run).
    const prior = contentJson?.redTeamAssessment?.current || null;

    // 4) Invoke the Red Team.
    const { data, error } = await supabase.functions.invoke('review-submitability', {
      body: {
        proposal,
        solicitation: {
          profile: profileObj,
          clins,
          complianceRequirements,
          contract: contract ? {
            title: contract.title, agency: contract.agency, naicsCode: contract.naicsCode,
            contractType: contract.contractType, dueDate: contract.dueDate, fundingAmount: contract.fundingAmount,
          } : undefined,
          solicitationText,
        },
        prior,
      },
    });
    if (error) throw new Error(error.message || 'Red Team review failed');
    if (data?.success === false) throw new Error(data.error || 'Red Team review failed');
    const verdict: SubmitabilityVerdict = data.verdict;

    // 5) Map onto the existing views + persist (latest + prior inline).
    const views = this.mapVerdictToViews(verdict);
    const newContentJson = {
      ...contentJson,
      redTeamAssessment: { current: verdict, ...(prior ? { prior } : {}) },
      gapReport: views.gapReport,
      winAssessment: views.winAssessment,
      improvementRoadmap: views.improvementRoadmap,
    };
    const { error: upErr } = await supabase
      .from('proposal_templates')
      .update({ content_json: newContentJson })
      .eq('id', proposalTemplateId)
      .eq('profile_id', profileId);
    if (upErr) throw new Error(`Failed to save Red Team review: ${upErr.message}`);

    return { verdict, contentJson: newContentJson };
  }

  /**
   * Build a requirement coverage matrix: map every binding solicitation
   * requirement to the proposal section that addresses it (covered / partial /
   * uncovered). Read-only assessment; persisted on content_json.requirementCoverage.
   */
  async checkRequirementCoverage(params: {
    proposalTemplateId: string;
    profileId: string;
    noticeId: string;
    contentJson: any;
    contract?: any;
  }): Promise<{ coverage: RequirementCoverage; contentJson: any }> {
    const { proposalTemplateId, profileId, noticeId, contentJson, contract } = params;

    // Assemble the proposal sections (title + raw content) for matching.
    const ts: Array<{ title: string; content: string }> = Array.isArray(contentJson?.technicalSections)
      ? contentJson.technicalSections : [];
    const sections = [
      { title: 'Cover Letter', text: contentJson?.coverLetter || '' },
      ...ts.map((s) => ({ title: s.title, text: s.content || '' })),
    ].filter((s) => (s.text || '').trim());

    // Canonical solicitation truth from the generation session (best-effort).
    let profileObj: any = null;
    let solicitationText = '';
    try {
      const session = await this.loadSession(profileId, noticeId);
      profileObj = session?.solicitationProfile || null;
      solicitationText = (session?.collectedData as any)?.solicitationText || '';
    } catch { /* fall back to deliverables data only */ }
    const dd = contentJson?.deliverablesData || {};
    const complianceRequirements =
      dd?.compliance_requirements || dd?.enriched_deliverables?.compliance_requirements || [];

    const { data, error } = await supabase.functions.invoke('check-requirement-coverage', {
      body: {
        sections,
        solicitation: {
          profile: profileObj,
          complianceRequirements,
          solicitationText,
          contract: contract ? { title: contract.title, agency: contract.agency, naicsCode: contract.naicsCode } : undefined,
        },
      },
    });
    if (error) throw new Error(error.message || 'Requirement coverage check failed');
    if (data?.success === false) throw new Error(data.error || 'Requirement coverage check failed');

    const coverage: RequirementCoverage = { ...data.coverage, assessedAt: new Date().toISOString() };
    const newContentJson = { ...contentJson, requirementCoverage: coverage };
    const { error: upErr } = await supabase
      .from('proposal_templates')
      .update({ content_json: newContentJson })
      .eq('id', proposalTemplateId)
      .eq('profile_id', profileId);
    if (upErr) throw new Error(`Failed to save requirement coverage: ${upErr.message}`);

    return { coverage, contentJson: newContentJson };
  }

  /**
   * Re-run the deliverables backbone for an existing proposal using the FULL
   * current pipeline (structure-price-schedule → schedule-primary extraction →
   * enrich → labor framework). Reconstructs inputs from the saved generation
   * session so it sees the real solicitation text + profile + CLIN schedule.
   * Returns null when there is no saved session to rebuild from.
   */
  async regenerateDeliverablesForProposal(params: {
    profileId: string; noticeId: string;
  }): Promise<DeliverablesData | null> {
    const session = await this.loadSession(params.profileId, params.noticeId);
    if (!session) return null;
    const collectedData: CollectedData = { ...session.collectedData, solicitationProfile: session.solicitationProfile };
    // Force the structurer to re-run fresh (don't reuse a stale cached schedule).
    delete (collectedData as any).priceSchedule;
    const laborData = await this.generateDeliverablesAnalysis(collectedData, session.contract);
    if (!laborData) return null;

    // Persist the fresh backbone + the structurer-seeded CLIN schedule back into the
    // session. ensurePriceSchedule mutated collectedData.solicitationProfile.pricing.clins
    // in place; without persisting, the Pricing Studio / Recompute Pricing (which read
    // getApprovedPayload(session,'backbone') and solicitation_profile.pricing.clins) keep
    // serving the PRIOR stale backbone — the exact low-ball staleness this regenerate fixes.
    session.solicitationProfile = collectedData.solicitationProfile ?? session.solicitationProfile;
    session.phases['backbone'] = {
      ...(session.phases['backbone'] || { approved: false }),
      status: 'approved',
      approved: true,
      approvedAt: new Date().toISOString(),
      payload: laborData,
      error: null,
      regenerateRequired: false,
    };
    // Deliverables changed → the priced schedule + cost rollup are now stale. Invalidate the
    // rate-dependent downstream gates so pricing re-runs against the new backbone (Recompute
    // Pricing is the cheap, deterministic way to refresh them).
    for (const p of ['pricing-compliance', 'summary-cover'] as GenerationPhaseId[]) {
      if (session.phases[p]) session.phases[p] = { status: 'not_started', approved: false };
    }
    await this.persistSession(session);
    return laborData;
  }

  /**
   * G0 entry point. Called when the user finishes gap resolution and clicks
   * Generate. Resumes an existing active session if one exists; otherwise creates
   * a fresh session already-approved at G0 (the enriched CollectedData snapshot +
   * the contract + acknowledged gaps + readiness essentials become the controlling
   * inputs for every downstream phase).
   */
  async createOrResumeSession(params: {
    profileId: string;
    noticeId: string;
    report: ReadinessReport;
    acknowledgedGaps: string[];
  }): Promise<GenerationSession> {
    const existing = await this.loadSession(params.profileId, params.noticeId);
    if (existing && existing.status === 'active') return existing;

    const { report } = params;
    const now = new Date().toISOString();
    const phases: Record<string, GenerationPhaseState> = {
      'gap-resolution': {
        status: 'approved',
        approved: true,
        approvedAt: now,
        payload: {
          contract: report.contract,
          acknowledgedGaps: params.acknowledgedGaps,
          overallScore: report.overallScore,
          gaps: report.gaps,
          matches: report.matches,
        },
      },
    };

    const row = {
      profile_id: params.profileId,
      notice_id: params.noticeId,
      contract_title: report.contract?.title ?? null,
      status: 'active',
      current_phase: 'backbone',
      collected_data: report.collectedData,
      solicitation_profile: report.collectedData?.solicitationProfile ?? null,
      phases,
      ai_questions: [],
      proposal_template_id: null,
    };

    // Upsert so re-starting after a completed/abandoned session reuses the row
    // (UNIQUE(profile_id, notice_id)) and resets phase state to fresh.
    const { data, error } = await supabase
      .from(OrchestratedProposalService.SESSIONS_TABLE)
      .upsert(row, { onConflict: 'profile_id,notice_id' })
      .select('*')
      .single();
    if (error) throw new Error(`Failed to create generation session: ${error.message}`);
    return this.sessionFromRow(data);
  }

  /**
   * Delete a session (used by "start over"). Returns the number of rows actually
   * deleted. `.select()` is required so the count comes back — without it a delete
   * that matches 0 rows (RLS, or a key mismatch) is indistinguishable from success,
   * which is exactly how "Start over" can silently resume a stale session. Errors
   * now throw instead of being swallowed.
   */
  async deleteSession(profileId: string, noticeId: string): Promise<number> {
    const { data, error } = await supabase
      .from(OrchestratedProposalService.SESSIONS_TABLE)
      .delete()
      .eq('profile_id', profileId)
      .eq('notice_id', noticeId)
      .select('id');
    if (error) throw new Error(`Failed to delete generation session: ${error.message}`);
    return data?.length ?? 0;
  }

  /**
   * Abandon an in-progress session (the in-flow twin of "start over"). Marks the
   * row `abandoned` so it no longer surfaces as resumable (listResumableSessions
   * filters on `status = 'active'`) and the dashboard "Resume" chip clears, while
   * keeping the row for audit. Used when a gate errors or was interrupted and the
   * user chooses not to retry.
   */
  async abandonSession(session: GenerationSession): Promise<void> {
    session.status = 'abandoned';
    await this.persistSession(session);
  }

  // Convert the backbone's derive-labor-rates clarifying_questions into the
  // normalized GenerationQuestion shape (impact: high -> critical).
  private mapBackboneQuestions(deliverables: DeliverablesData | null): GenerationQuestion[] {
    const qs = deliverables?.ai_pricing?.clarifying_questions || [];
    return qs.map((q: any) => ({
      id: q.id || `backbone-${q.field || Math.random().toString(36).slice(2)}`,
      phase: 'backbone' as GenerationPhaseId,
      question: q.question,
      severity: q.impact === 'high' ? 'critical' : 'optional',
      answer: null,
      field: q.field,
      whyItMatters: q.why_it_matters,
      currentAssumption: q.current_assumption,
      inputType: q.input_type,
      options: q.options ?? null,
    }));
  }

  // Replace a phase's questions while preserving any answers the user already gave.
  private mergeQuestions(
    existing: GenerationQuestion[],
    incoming: GenerationQuestion[],
    phase: GenerationPhaseId
  ): GenerationQuestion[] {
    const answersById = new Map(existing.filter(q => q.answer).map(q => [q.id, q.answer]));
    const others = existing.filter(q => q.phase !== phase);
    const merged = incoming.map(q => ({ ...q, answer: answersById.get(q.id) ?? q.answer ?? null }));
    return [...others, ...merged];
  }

  private getApprovedPayload(session: GenerationSession, phaseId: GenerationPhaseId): any {
    return session.phases[phaseId]?.payload ?? null;
  }

  // Rebuild a GeneratedProposal-shaped object from the session's persisted phase
  // payloads (NOT an in-memory accumulator) so the G5 digest and the final
  // assembly work identically on a fresh resume.
  /**
   * Re-synthesize the Product Definition from the ENTIRE generated proposal (not
   * just the backbone). The first product synthesis happens at backbone time with
   * only deliverables+labor, so it's necessarily thin. Once the technical,
   * management, past-performance, personnel, pricing, and compliance sections
   * exist, this feeds all of them (as a text digest) into synthesize-product so the
   * Product reflects the whole, approved proposal. Resilient: returns null on any
   * failure so the existing backbone product is kept.
   */
  private async synthesizeFullProduct(
    collectedData: CollectedData,
    contract: ContractData,
    deliverablesData: any,
    sectionsDigest: string
  ): Promise<any | null> {
    if (!deliverablesData) return null;
    try {
      const deliverables = (deliverablesData.deliverables_with_labor || []).map((d: any) => ({
        id: d.deliverable_id,
        name: d.deliverable_name,
        description: d.description,
        type: d.type,
        category: d.category,
        quantity: d.quantity,
        unit: d.unit,
        specifications: d.specifications,
      }));
      if (deliverables.length === 0) return null;
      const { data, error } = await supabase.functions.invoke('synthesize-product', {
        body: {
          deliverables,
          compliance_requirements:
            deliverablesData.compliance_framework?.requirements ||
            deliverablesData.compliance_requirements || [],
          labor_summary: deliverablesData.labor_summary,
          contract_metadata: {
            notice_id: contract.id,
            title: contract.title,
            naics_code: contract.naicsCode || '',
            agency: contract.agency,
            contract_type: contract.contractType || 'service',
            estimated_value: contract.fundingAmount || 0,
            period_of_performance: deliverablesData.timeline_summary?.period_of_performance,
            set_aside_type: contract.setAsides?.[0] || null,
          },
          rfq_summary: collectedData.solicitationText?.substring(0, 2000),
          // The whole approved proposal — what makes the product comprehensive.
          generated_sections: sectionsDigest,
          user_corrections: this.correctionTexts(collectedData),
        },
      });
      if (error) {
        console.warn('⚠️ Full product re-synthesis warning (keeping backbone product):', error);
        return null;
      }
      // The edge returns { product_definition: {...} }; tolerate both shapes.
      const product = data?.product_definition ?? data;
      return product && product.product_name ? product : null;
    } catch (e) {
      console.warn('⚠️ Full product re-synthesis error (keeping backbone product):', e);
      return null;
    }
  }

  private assembleProposalFromSession(session: GenerationSession): GeneratedProposal {
    const backbone = this.getApprovedPayload(session, 'backbone');
    const tm = session.phases['technical-management']?.payload || {};
    const pp = session.phases['pp-personnel']?.payload || {};
    const pc = session.phases['pricing-compliance']?.payload || {};
    const sc = session.phases['summary-cover']?.payload || {};
    const proposal: GeneratedProposal = {
      coverLetter: sc.coverLetter || null,
      executiveSummary: sc.executiveSummary || null,
      technical: tm.technical || null,
      management: tm.management || null,
      pastPerformance: pp.pastPerformance || null,
      personnel: pp.personnel || null,
      pricing: pc.pricing || null,
      compliance: pc.compliance || null,
      aiFeedback: null,
      deliverablesData: backbone || null,
    };
    // Fold in any inline sub-section edits so a user's edit to (say) the technical
    // section is what the downstream summary/cover digest sees — "whatever we
    // settle on is what feeds the next section."
    this.applyEditsToProposal(proposal, session);
    return proposal;
  }

  /**
   * Generate exactly ONE phase, persist its output + AI questions, then stop at
   * `awaiting_review` (or `needs_input` if it surfaced unanswered critical
   * questions). Inputs are reconstructed from the session, never from memory.
   */
  async runPhase(phaseId: GenerationPhaseId, session: GenerationSession): Promise<GenerationSession> {
    if (phaseId === 'gap-resolution') return session; // G0 handled by GapResolutionPanel

    session.phases[phaseId] = {
      ...(session.phases[phaseId] || { approved: false }),
      status: 'generating',
      error: null,
    };
    session.currentPhase = phaseId;
    await this.persistSession(session);
    this.updateProgress({
      phase: 'generating',
      currentSection: phaseId,
      completedSections: GENERATION_PHASE_ORDER.filter(p => session.phases[p]?.approved),
      totalSections: GENERATION_PHASE_ORDER.length,
      percentage: 50,
      message: `Generating ${GENERATION_PHASE_META[phaseId].label}...`,
    });

    // CollectedData carries the authoritative solicitation profile for every phase.
    const collectedData: CollectedData = { ...session.collectedData, solicitationProfile: session.solicitationProfile };
    const contract = session.contract;

    try {
      let payload: any;
      let incomingQuestions: GenerationQuestion[] = [];

      if (phaseId === 'backbone') {
        const deliverables = await this.generateDeliverablesAnalysis(collectedData, contract);
        payload = deliverables;
        incomingQuestions = this.mapBackboneQuestions(deliverables);
      } else if (phaseId === 'technical-management') {
        const deliverablesData = this.getApprovedPayload(session, 'backbone');
        // Pass `session` → runs async (background task + poll) so this heavy, high-effort
        // gate never hits the edge function's 150s request idle timeout.
        const r = await this.generateTechnicalManagement(collectedData, contract, deliverablesData, session);
        payload = { technical: r.technical, management: r.management };
      } else if (phaseId === 'pp-personnel') {
        const r = await this.generatePastPerformancePersonnel(collectedData, contract);
        payload = { pastPerformance: r.pastPerformance, personnel: r.personnel };
      } else if (phaseId === 'pricing-compliance') {
        const deliverablesData = this.getApprovedPayload(session, 'backbone');
        const r = await this.generatePricingCompliance(collectedData, contract, deliverablesData);
        payload = { pricing: r.pricing, compliance: r.compliance };
      } else if (phaseId === 'summary-cover') {
        const proposalForDigest = this.assembleProposalFromSession(session);
        const digest = this.buildGeneratedSectionsDigest(proposalForDigest);
        // Now that every section exists, re-synthesize the Product from the ENTIRE
        // proposal (the backbone product was built from deliverables alone). Persist
        // it onto the backbone payload so the finalized Product reflects everything.
        try {
          const richProduct = await this.synthesizeFullProduct(
            collectedData, contract, proposalForDigest.deliverablesData, digest
          );
          if (richProduct?.product_name && session.phases['backbone']?.payload) {
            session.phases['backbone'].payload.product_definition = richProduct;
            console.log('✅ Product re-synthesized from full proposal');
          }
        } catch (e) {
          console.warn('⚠️ Full product re-synthesis skipped:', e);
        }
        const [exec, cover] = await Promise.all([
          this.withTimeout(this.generateExecutiveSummary(collectedData, contract, digest), 300000, 'Executive Summary generation'),
          this.withTimeout(this.generateCoverLetter(collectedData, contract, digest), 300000, 'Cover Letter generation'),
        ]);
        payload = { executiveSummary: exec, coverLetter: cover };
      }

      // Defensive: any phase may emit clarifying_questions in the future.
      if (incomingQuestions.length > 0) {
        session.questions = this.mergeQuestions(session.questions, incomingQuestions, phaseId);
      }
      const hasUnansweredCritical = session.questions.some(
        q => q.phase === phaseId && q.severity === 'critical' && !q.answer
      );

      session.phases[phaseId] = {
        ...session.phases[phaseId],
        status: hasUnansweredCritical ? 'needs_input' : 'awaiting_review',
        payload,
        approved: false,
        error: null,
        // Fresh generation incorporates the latest answers, so clear the marker.
        regenerateRequired: false,
      };
      await this.persistSession(session);
      return session;
    } catch (e: any) {
      session.phases[phaseId] = {
        ...(session.phases[phaseId] || { approved: false }),
        status: 'error',
        error: e?.message || String(e),
      };
      await this.persistSession(session);
      throw e;
    }
  }

  /** Record a user's answer to an AI question and route pricing answers to the
   *  backbone's rate-derivation input. */
  async answerQuestion(session: GenerationSession, questionId: string, answer: string): Promise<GenerationSession> {
    session.questions = session.questions.map(q => q.id === questionId ? { ...q, answer } : q);
    const q = session.questions.find(x => x.id === questionId);
    if (q && q.phase === 'backbone') {
      const ac: any = session.collectedData.additionalContext || {};
      const store = (ac.pricingAnswers && typeof ac.pricingAnswers === 'object') ? { ...ac.pricingAnswers } : {};
      store[q.field || q.id] = answer;
      session.collectedData.additionalContext = { ...ac, pricingAnswers: store } as any;
    }
    // The answer only takes effect on the next runPhase, so if this phase was
    // already generated, require a regenerate before it can be approved.
    if (q) {
      const ph = session.phases[q.phase];
      if (ph && ph.payload && !ph.approved) {
        session.phases[q.phase] = { ...ph, status: 'needs_input', regenerateRequired: true };
      }
    }
    await this.persistSession(session);
    return session;
  }

  /** Approve a phase (gated on unanswered critical questions) and advance the
   *  cursor to the next gate. */
  async approvePhase(session: GenerationSession, phaseId: GenerationPhaseId): Promise<GenerationSession> {
    const ph = session.phases[phaseId];
    if (!ph || ph.status === 'generating') {
      throw new Error('Phase is not ready to approve.');
    }
    if (ph.regenerateRequired) {
      throw new Error('Regenerate this phase to apply your answers before approving.');
    }
    const blocked = session.questions.some(q => q.phase === phaseId && q.severity === 'critical' && !q.answer);
    if (blocked) {
      throw new Error('Answer the critical questions for this phase before approving.');
    }
    session.phases[phaseId] = { ...ph, status: 'approved', approved: true, approvedAt: new Date().toISOString() };
    const idx = GENERATION_PHASE_ORDER.indexOf(phaseId);
    const next = GENERATION_PHASE_ORDER[idx + 1];
    session.currentPhase = next ?? 'finalize';
    await this.persistSession(session);
    return session;
  }

  /** Regenerate a phase: invalidates this phase + clears all downstream phases
   *  (their inputs are now stale), then re-runs this phase. */
  async regeneratePhase(session: GenerationSession, phaseId: GenerationPhaseId, opts?: { forceFull?: boolean }): Promise<GenerationSession> {
    // Cost saver: when the backbone is regenerated solely to APPLY rate answers (the only
    // backbone questions come from rate derivation, and answering sets regenerateRequired),
    // only the rate math + cost rollup change — re-running the full 5-step pipeline (Gemini
    // extract, GPT-4o enrich, Sonnet labor framework, Gemini product, GPT-4o rates) is pure
    // waste. Reprice in place (rate-derivation step only). forceFull (used by feedback
    // corrections, which can change deliverables) bypasses this.
    if (!opts?.forceFull
        && phaseId === 'backbone'
        && session.phases['backbone']?.payload
        && session.phases['backbone']?.regenerateRequired) {
      return this.repriceBackbone(session);
    }
    const idx = GENERATION_PHASE_ORDER.indexOf(phaseId);
    for (let i = idx + 1; i < GENERATION_PHASE_ORDER.length; i++) {
      const p = GENERATION_PHASE_ORDER[i];
      if (session.phases[p]) {
        session.phases[p] = { status: 'not_started', approved: false };
      }
    }
    // Drop this phase's stale questions so re-run re-derives them.
    session.questions = session.questions.filter(q => q.phase !== phaseId);
    await this.persistSession(session);
    return this.runPhase(phaseId, session);
  }

  /**
   * Re-run ONLY the rate-derivation step of the backbone against the already-generated
   * deliverables / labor framework / product, applying the user's rate answers from
   * additionalContext.pricingAnswers. Avoids the costly full 5-step re-run when only
   * fringe/overhead/G&A/fee/locality/etc. changed, and invalidates ONLY the rate-dependent
   * downstream gates (pricing-compliance, summary-cover) — technical/pp-personnel don't
   * depend on rates.
   */
  private async repriceBackbone(session: GenerationSession): Promise<GenerationSession> {
    const existing = session.phases['backbone']?.payload as DeliverablesData | undefined;
    if (!existing) {
      // Nothing to reuse — fall back to a full backbone run.
      return this.runPhase('backbone', session);
    }
    const collectedData: CollectedData = { ...session.collectedData, solicitationProfile: session.solicitationProfile };
    const contract = session.contract;

    const aiPricing = await this.deriveLaborRates(collectedData, contract, existing.labor_summary);
    const payload: DeliverablesData = {
      ...existing,
      ai_pricing: aiPricing,
      metadata: { ...(existing.metadata || {}), repricedAt: new Date().toISOString() },
    };

    const incomingQuestions = this.mapBackboneQuestions(payload);
    session.questions = this.mergeQuestions(session.questions, incomingQuestions, 'backbone');
    const hasUnansweredCritical = session.questions.some(
      q => q.phase === 'backbone' && q.severity === 'critical' && !q.answer
    );

    session.phases['backbone'] = {
      ...session.phases['backbone'],
      status: hasUnansweredCritical ? 'needs_input' : 'awaiting_review',
      payload,
      approved: false,
      error: null,
      regenerateRequired: false,
    };

    // Repricing changes the cost rollup → invalidate ONLY rate-dependent downstream gates.
    for (const p of ['pricing-compliance', 'summary-cover'] as GenerationPhaseId[]) {
      if (session.phases[p]) session.phases[p] = { status: 'not_started', approved: false };
    }

    await this.persistSession(session);
    return session;
  }

  /** Record an AUTHORITATIVE user correction at a gate. Corrections are global:
   *  stored on the session at additionalContext.userCorrections, folded into
   *  governingRules (every narrative section) and threaded into the backbone
   *  edge prompts, so they compound into the current + all downstream steps. */
  async addCorrection(session: GenerationSession, phaseId: GenerationPhaseId, text: string): Promise<GenerationSession> {
    const clean = (text || '').trim();
    if (!clean) return session;
    const ac: any = session.collectedData.additionalContext || {};
    const list = Array.isArray(ac.userCorrections) ? [...ac.userCorrections] : [];
    list.push({
      id: `corr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      phase: phaseId,
      text: clean,
      at: new Date().toISOString(),
    });
    session.collectedData.additionalContext = { ...ac, userCorrections: list } as any;
    await this.persistSession(session);
    return session;
  }

  /** Remove a previously-recorded correction by id. */
  async removeCorrection(session: GenerationSession, id: string): Promise<GenerationSession> {
    const ac: any = session.collectedData.additionalContext || {};
    if (!Array.isArray(ac.userCorrections)) return session;
    session.collectedData.additionalContext = {
      ...ac,
      userCorrections: ac.userCorrections.filter((c: any) => c?.id !== id),
    } as any;
    await this.persistSession(session);
    return session;
  }

  /** Record a free-text correction globally, then regenerate ONLY this gate with
   *  the correction now live in its grounding (downstream gates reset, not re-run). */
  async regeneratePhaseWithFeedback(
    session: GenerationSession,
    phaseId: GenerationPhaseId,
    feedback: string
  ): Promise<GenerationSession> {
    await this.addCorrection(session, phaseId, feedback);
    // forceFull: a correction can change deliverables/scope, so it needs the full re-run,
    // not the rate-only reprice shortcut.
    return this.regeneratePhase(session, phaseId, { forceFull: true });
  }

  /**
   * Cheap, near-zero-credit refresh of ONLY the pricing + compliance section against the
   * already-stored grounded data. `generate-pricing-compliance` is deterministic (no LLM),
   * so this re-invokes it with the persisted backbone + solicitation profile and returns
   * the refreshed sections — letting the Viewer pick up edge-function changes (cover-sheet
   * fills, floor reconciliation, classifier/de-leak) WITHOUT a full regeneration.
   *
   * If the session never captured a solicitation profile (e.g. extraction was failing when
   * it was generated), re-extract it first — one fast Haiku call — so the vehicle/min-max
   * and floor reconciliation can populate. Returns null when there is no persisted session
   * for this proposal (legacy one-shot); the caller then falls back to full regeneration.
   */
  async recomputePricing(
    profileId: string,
    noticeId: string
  ): Promise<{ pricing: ProposalSection; compliance: ProposalSection } | null> {
    const session = await this.loadSession(profileId, noticeId);
    if (!session) return null;

    const collectedData: CollectedData = { ...session.collectedData, solicitationProfile: session.solicitationProfile };

    // The vehicle type + guaranteed min/max (and thus the floor reconciliation) come from
    // the solicitation profile. If it was never captured, re-extract it once and persist it
    // back onto the session. Best-effort: price without it rather than fail.
    if (!collectedData.solicitationProfile && (collectedData.solicitationText || '').length > 100) {
      try {
        const profile = await this.withTimeout(
          this.extractSolicitationProfile(collectedData, session.contract),
          120000,
          'Solicitation profile extraction'
        );
        if (profile) {
          collectedData.solicitationProfile = profile;
          session.solicitationProfile = profile;
          session.collectedData = { ...session.collectedData, solicitationProfile: profile };
          await this.persistSession(session);
        }
      } catch (e) {
        console.warn('⚠️ recomputePricing: profile re-extraction failed; pricing without floor data:', e);
      }
    }

    const deliverablesData = this.getApprovedPayload(session, 'backbone') as DeliverablesData | null;
    const r = await this.generatePricingCompliance(collectedData, session.contract, deliverablesData);

    // NOTE: we intentionally do NOT persist the recomputed pricing onto the session here.
    // The caller (Viewer) shows a review modal and applies the result only on accept, so a
    // rejected recompute leaves everything untouched. (The re-extracted profile above IS
    // persisted — it's strictly better grounded data and avoids re-extracting on accept.)
    return { pricing: r.pricing, compliance: r.compliance };
  }

  /** Persist a per-sub-section inline edit (applied over the payload at finalize). */
  async saveSubSectionEdit(
    session: GenerationSession,
    phaseId: GenerationPhaseId,
    key: string,
    value: string
  ): Promise<GenerationSession> {
    const ph = session.phases[phaseId] || { status: 'awaiting_review', approved: false };
    session.phases[phaseId] = { ...ph, edits: { ...(ph.edits || {}), [key]: value } };
    await this.persistSession(session);
    return session;
  }

  // Apply persisted sub-section edits over the assembled proposal content.
  private applyEditsToProposal(proposal: GeneratedProposal, session: GenerationSession): void {
    const fieldMap: Record<string, keyof GeneratedProposal> = {
      technical: 'technical',
      management: 'management',
      pastPerformance: 'pastPerformance',
      personnel: 'personnel',
      pricing: 'pricing',
      compliance: 'compliance',
      executiveSummary: 'executiveSummary',
      coverLetter: 'coverLetter',
    };
    for (const phaseId of GENERATION_PHASE_ORDER) {
      const edits = session.phases[phaseId]?.edits;
      if (!edits) continue;
      for (const [key, value] of Object.entries(edits)) {
        const field = fieldMap[key];
        const section = field ? (proposal[field] as ProposalSection | null) : null;
        if (section) section.content = value;
      }
    }
  }

  /**
   * Finalize: assemble the approved phases (with edits), run optional AI feedback,
   * and write the trigger-valid final proposal to proposal_templates (the existing
   * delete-before-insert path). Marks the session complete.
   */
  async finalizeSession(session: GenerationSession): Promise<{ proposal: GeneratedProposal; templateId: string }> {
    const collectedData: CollectedData = { ...session.collectedData, solicitationProfile: session.solicitationProfile };
    const contract = session.contract;
    const proposal = this.assembleProposalFromSession(session); // already includes inline edits

    try {
      const ack = session.phases['gap-resolution']?.payload?.acknowledgedGaps || [];
      proposal.aiFeedback = await this.generateAIFeedback(collectedData, contract, proposal, ack);
    } catch (e) {
      console.warn('AI feedback failed at finalize (continuing without):', e);
      proposal.aiFeedback = null;
    }

    const g0 = session.phases['gap-resolution']?.payload || {};
    const record = buildProposalTemplateRecord({
      proposal,
      profileId: session.profileId,
      noticeId: session.noticeId,
      contractTitle: session.contractTitle || contract?.title || 'Proposal',
      overallScore: g0.overallScore ?? 0,
      gaps: g0.gaps ?? null,
      matches: g0.matches ?? null,
    });

    // delete-before-insert respects proposal_templates' UNIQUE(profile_id, notice_id)
    await supabase
      .from('proposal_templates')
      .delete()
      .eq('profile_id', session.profileId)
      .eq('notice_id', session.noticeId);
    const { data, error } = await supabase
      .from('proposal_templates')
      .insert(record)
      .select('id')
      .single();
    if (error) throw new Error(`Failed to save final proposal: ${error.message}`);

    session.proposalTemplateId = data.id;
    session.status = 'complete';
    session.currentPhase = 'finalize';
    await this.persistSession(session);
    return { proposal, templateId: data.id };
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Convert file to base64
   */
  static async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  }

  /**
   * Validate file size (max 2MB)
   */
  static validateFileSize(file: File): boolean {
    const maxSize = 2 * 1024 * 1024; // 2MB
    return file.size <= maxSize;
  }

  /**
   * Get supported file types
   */
  static getSupportedFileTypes(): string[] {
    return [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'text/csv'
    ];
  }
}

// ============================================================================
// SHARED: build the trigger-valid proposal_templates record
//
// The proposal_templates validate_proposal_content_trigger REQUIRES content_json
// to contain coverLetter, technicalSections, and feedback. Both the legacy
// one-shot store path (OrchestratedProposalGenerator.storeProposal) and the
// checkpoint finalizeSession() must emit exactly this shape, so it lives here.
// ============================================================================
export function buildProposalTemplateRecord(args: {
  proposal: GeneratedProposal;
  profileId: string;
  noticeId: string;
  contractTitle: string;
  overallScore: number;
  gaps: any;
  matches: any;
}): {
  profile_id: string;
  notice_id: string;
  template_name: string;
  template_url: string;
  content_json: Record<string, any>;
} {
  const { proposal } = args;
  return {
    profile_id: args.profileId,
    notice_id: args.noticeId,
    template_name: `Proposal: ${args.contractTitle}`,
    template_url: `orchestrated-proposals/${args.noticeId}/${Date.now()}/proposal.pdf`,
    content_json: {
      coverLetter: proposal.coverLetter?.content || '',
      technicalSections: [
        { title: 'Executive Summary', content: proposal.executiveSummary?.content || '' },
        { title: 'Technical Approach', content: proposal.technical?.content || '' },
        { title: 'Management Approach', content: proposal.management?.content || '' },
        { title: 'Past Performance', content: proposal.pastPerformance?.content || '' },
        { title: 'Key Personnel', content: proposal.personnel?.content || '' },
        { title: 'Pricing & Cost Strategy', content: proposal.pricing?.content || '' },
        { title: 'FAR/DFARS Compliance', content: proposal.compliance?.content || '' },
      ],
      feedback: {
        type: 'orchestrated',
        readinessScore: args.overallScore,
        gaps: args.gaps,
        matches: args.matches,
      },
      aiFeedback: proposal.aiFeedback || null,
      deliverablesData: proposal.deliverablesData || null,
      executiveSummary: proposal.executiveSummary?.content || '',
      technicalApproach: proposal.technical?.content || '',
      managementApproach: proposal.management?.content || '',
      pastPerformance: proposal.pastPerformance?.content || '',
      personnel: proposal.personnel?.content || '',
      pricingStrategy: proposal.pricing?.content || '',
      compliance: proposal.compliance?.content || '',
      proposalType: 'orchestrated',
      generatedAt: new Date().toISOString(),
    },
  };
}
